/**
 * Assemblage de l'application.
 *
 * Relie la carte, la coque, le tiroir, les réglages et le stockage. La logique
 * métier reste dans `core/` ; ce fichier ne fait que du câblage.
 */

import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl'

import {
  DEFAULT_CAMERA,
  GUIDANCE_TILT,
  GUIDANCE_ZOOM,
  getStyleDescriptor,
  getStyleUrl,
  type MapStyleId,
} from './core/mapStyles'
import {
  GpxTrack,
  ownsFileWaypointsAtImport,
  restoredFileWaypoints,
  totalDistanceOf,
} from './core/track'
import {
  GPX_SNIFF_BYTES,
  assembledSourceType,
  isGpxFileName,
  looksLikeGpx,
} from './core/gpxParser'
import type { ParsedTrack } from './core/gpxParser'
import { parseGpxAsync } from './core/gpxWorker'
import { currentPickerEnvironment, gpxAcceptAttribute } from './core/filePicker'
import { sanitizeFileName, writeGpx } from './core/gpxWriter'
import type { TrackToWrite } from './core/gpxWriter'
import { patchGpx } from './core/gpxPatch'
import { isSparseTrack, medianPointSpacing } from './core/routing'
import { pickTrackColor, TRACK_COLORS } from './core/trackColors'
import { PreferencesStore } from './core/preferences'
import { PUBLISHER } from './core/publisher'
import { Storage, type StoredTrack } from './core/storage'
import { metersPerPixel } from './core/geo'
import {
  LIMITS,
  MARKER_TYPE_WAYOUT,
  ROUTING_PROFILES,
  normalizeMarkerType,
  routingProfileFromName,
} from './core/types'
import type { CustomMarker, TrackSegment, Waypoint } from './core/types'
import { Translator, resolveLocale } from './i18n'
import { MapLayerManager } from './ui/mapLayers'
import { placeMenu } from './ui/menuPlacement'
import { createElevationChart } from './ui/elevationChart'
import { Shell } from './ui/shell'
import { Drawer } from './ui/drawer'
import { SettingsScreen } from './ui/settings'
import { chooseDialog, confirmDialog, promptDialog, showDialog } from './ui/dialogs'
import { canShareFile, downloadFile, shareFile } from './ui/fileExport'
import { TrackSettingsScreen } from './ui/trackSettings'
import { WayInOutController } from './ui/wayInOutController'
import { MapPickController } from './ui/mapPick'
import { enableWayViaDrag } from './ui/wayViaDrag'
import { RadarManager } from './core/radars'
import { ScreenWakeLock } from './core/wakeLock'
import { RadarController } from './ui/radarController'
import { ElevationController } from './ui/elevationController'
import { RouteController } from './ui/routeController'
import {
  createIcon,
  navigationArrowMarkup,
  positionDotMarkup,
} from './ui/icons'
import { LocationTracker, type LocationSample } from './core/location'
import { NavigationController } from './core/navigation'
import {
  LocalProgressionHintStore,
  SnappedPositionTracker,
} from './core/snappedPositionTracker'
import { createMapRoadQuery } from './ui/roadQuery'
import { LiveHudController } from './ui/liveHud'
import { locationIconSize } from './core/preferences'
import { GeoidCorrector } from './core/geoid'
import { MarkerController } from './ui/markerController'
import { onMapLongPress } from './ui/longPress'

export const APP_VERSION = '0.1.0'

/**
 * Durée de l'animation des boutons de zoom.
 *
 * Celle de MapLibre par défaut ; nommée ici parce qu'elle conditionne la
 * fenêtre pendant laquelle les clics doivent se cumuler.
 */
const ZOOM_BUTTON_DURATION_MS = 300

/**
 * Lissage du zoom piloté par la boucle d'affichage.
 *
 * Même coefficient que celui de la position : les deux convergent alors au
 * même rythme, et un zoom demandé pendant un déplacement ne donne pas
 * l'impression de deux mouvements désaccordés.
 */
const ZOOM_EASE_ALPHA = 0.15

/** Écart en deçà duquel le zoom est posé sur sa cible. */
const ZOOM_SNAP = 0.01

/**
 * Marge haute réservée pendant le suivi, en fraction de la hauteur.
 *
 * Elle place la position aux ~77 % de la hauteur — 55 % de marge, plus la
 * moitié des 45 % restants. Ce qui reste à parcourir compte davantage que ce
 * qui est déjà derrière, et c'est vrai dès le suivi simple, pas seulement en
 * guidage : `MapManager` l'applique sans condition dans sa boucle caméra.
 */
const FOLLOW_TOP_PADDING_RATIO = 0.55

/**
 * Hauteur à laquelle la position se retrouve, en fraction de l'écran.
 *
 * Déduite de la marge plutôt que fixée à 0,775 : la valeur est le centre de
 * la zone restante, les deux ne peuvent pas diverger.
 */
const FOLLOW_POSITION_RATIO =
  FOLLOW_TOP_PADDING_RATIO + (1 - FOLLOW_TOP_PADDING_RATIO) / 2

/*
 * Aucune marge basse n'est réservée pendant le suivi.
 *
 * Un essai a consisté à en poser une sur les écrans courts, pour écarter la
 * position du HUD vitesse/altitude qui la serre de près sur iPhone. C'était
 * corriger le mauvais côté de la collision : remonter la position la sort de
 * la place que lui donne `FOLLOW_TOP_PADDING_RATIO`, réglée pour qu'on voie
 * loin devant en roulant. Essayé et rejeté à l'usage, sur les trois modes —
 * la position se retrouvait trop haute partout. Si l'écart au HUD doit se
 * regagner, c'est le HUD qu'il faut déplacer.
 */

/** Écart en deçà duquel l'inclinaison est posée sur sa cible, en degrés. */
const PITCH_SNAP = 0.1

/**
 * Mise en veille de la boucle caméra : images immobiles à attendre, puis
 * intervalle en veille.
 *
 * Repris de `MapManager.kt` — une seconde à 60 Hz, puis 10 Hz. Repositionner
 * une caméra identique n'est pas gratuit : MapLibre réveille son rendu et ses
 * écouteurs de mouvement à chaque appel. À l'arrêt, un feu rouge ou une pause,
 * c'est la batterie du téléphone qui paie.
 */
const IDLE_FRAME_THRESHOLD = 60
const IDLE_FRAME_INTERVAL_MS = 100

/**
 * Réduction de la flèche par rapport au point, en suivi.
 *
 * À taille égale, une flèche pleine et orientée pèse visuellement plus lourd
 * qu'un point : posée aux trois quarts de l'écran et en mouvement, elle
 * mangeait la route devant. Le réglage de taille continue de commander les
 * deux, ce rapport ne fait que les accorder.
 */
const FOLLOW_ICON_RATIO = 0.85

/**
 * Réduction du point hors suivi.
 *
 * Le réglage donne un repère plus gros que celui d'Android, dont le mode
 * `NORMAL` dessine un cerclage de 22 dp sans jamais consulter la taille
 * choisie — celle-ci n'alimente que `gpsDrawable`, la flèche. Ce rapport
 * ramène le point à ces 22 px au réglage par défaut tout en laissant le
 * curseur commander les deux repères.
 */
const IDLE_ICON_RATIO = 0.8

/** Couleurs proposées dans le sélecteur, en plus de la palette d'attribution. */
const EXTRA_COLORS = [
  '#000000',
  '#607D8B',
  '#795548',
  '#FFEB3B',
  '#4CAF50',
  '#2196F3',
  '#9C27B0',
  '#F44336',
] as const

export class VeloGpxApp {
  readonly #prefs = new PreferencesStore()
  readonly #storage = new Storage()
  readonly #wakeLock = new ScreenWakeLock()
  readonly #translator: Translator
  readonly #geoid = new GeoidCorrector()

  #map!: MapLibreMap
  #layers!: MapLayerManager
  #shell!: Shell
  #drawer!: Drawer
  #settings!: SettingsScreen
  #location!: LocationTracker

  #navigation!: NavigationController
  #tracker!: SnappedPositionTracker
  #liveHud!: LiveHudController
  #markers!: MarkerController
  #trackSettings!: TrackSettingsScreen
  #ways!: WayInOutController
  #radarManager!: RadarManager
  #radars!: RadarController
  #elevation!: ElevationController
  #route!: RouteController
  #picker!: MapPickController
  #puck: maplibregl.Marker | null = null
  #frameHandle: number | null = null

  /**
   * Minuteur de la boucle en veille, et son drapeau de marche.
   *
   * En veille, `#frameHandle` est nul entre deux images sans que la boucle
   * soit arrêtée pour autant : c'est `#cameraLoopRunning` qui répond à la
   * question « la caméra est-elle pilotée ? », et lui seul.
   */
  #idleTimer: number | null = null
  #cameraLoopRunning = false
  #idleFrames = 0
  #fileInput: HTMLInputElement | null = null
  #selectionHandled = false

  #tracks: GpxTrack[] = []
  #storedByTrackId = new Map<string, StoredTrack>()

  #following = false
  #guidance = false

  /** Fond de carte réellement posé, pour détecter un changement de réglage. */
  #appliedStyle: MapStyleId

  /** Un échange de style est en cours : les réglages attendent son issue. */
  #styleSwapping = false

  constructor(
    private readonly appElement: HTMLElement,
    private readonly mapElement: HTMLElement,
    private readonly overlayElement: HTMLElement,
  ) {
    this.#translator = new Translator(
      resolveLocale(this.#prefs.get('language')),
    )
    this.#appliedStyle = this.#prefs.get('mapStyle')
  }

  async start(): Promise<void> {
    this.#applyTheme()
    this.#translator.setLocale(resolveLocale(this.#prefs.get('language')))

    // L'application est démasquée **avant** la création de la carte : MapLibre
    // mesure son conteneur à la construction, et un conteneur masqué le fait
    // retomber sur une taille par défaut de 400 × 300 px qu'il ne corrigera
    // jamais tout seul. L'écran de démarrage reste au-dessus le temps du
    // chargement.
    this.appElement.hidden = false

    this.#map = new maplibregl.Map({
      container: this.mapElement,
      style: getStyleUrl(this.#prefs.get('mapStyle')),
      center: DEFAULT_CAMERA.center,
      zoom: DEFAULT_CAMERA.zoom,
      attributionControl: { compact: true },
      maxPitch: 60,
      // La densité n'est délibérément **pas** plafonnée : MapLibre retombe sur
      // `devicePixelRatio`, et la carte est dessinée à la résolution de
      // l'écran comme le fait le SDK Android. Un plafond à 2 la faisait rendre
      // à 2× puis étirer à 3× par le navigateur sur la plupart des téléphones
      // récents — un tiers de résolution perdu, mesuré au canevas, et un fond
      // de carte visiblement plus mou que les symboles posés dessus, eux
      // fabriqués à la densité réelle.
    })

    this.#layers = new MapLayerManager(this.#map, {
      markerScale: () => this.#prefs.get('markerScale'),
      kmMarkerInterval: () => this.#prefs.get('kmMarkerInterval'),
      showKmMarkers: () => this.#prefs.get('kmMarkersEnabled'),
      customMarkerDarkMode: () => this.#prefs.get('customMarkerDarkMode'),
    })

    this.#buildUi()

    this.#radarManager = new RadarManager(this.#storage)
    this.#radars = new RadarController({
      shell: this.#shell,
      translator: this.#translator,
      prefs: this.#prefs,
      radars: this.#radarManager,
      onDisplay: (visible) =>
        this.#layers.setRadars(this.#radarManager.radars, visible),
      onTrackChanged: (track) => {
        this.#layers.updateTrack(track)
        this.#liveHud?.refresh()
        void this.#persistTracks()
      },
    })

    this.#elevation = new ElevationController({
      shell: this.#shell,
      translator: this.#translator,
      storage: this.#storage,
      tracks: () => this.#tracks,
      onTrackChanged: (track) => {
        this.#layers.updateTrack(track)
        this.#liveHud?.refresh()
        this.#refreshTrackList()
        void this.#persistTracks()
      },
      onProgress: () => this.#refreshTrackList(),
    })

    this.#route = new RouteController({
      shell: this.#shell,
      translator: this.#translator,
      storage: this.#storage,
      tracks: () => this.#tracks,
      onTrackChanged: (track) => {
        this.#layers.updateTrack(track)
        this.#liveHud?.refresh()
        this.#refreshTrackList()
        void this.#persistTracks()
      },
      onProgress: () => this.#refreshTrackList(),
      originalSegments: (track) => this.#reloadOriginalSegments(track),
      restoreElevations: (track) => this.#elevation.restore(track),
    })

    this.#picker = new MapPickController({
      map: this.#map,
      shell: this.#shell,
      cancelLabel: this.#translator.t('cancel'),
    })

    this.#ways = new WayInOutController({
      shell: this.#shell,
      translator: this.#translator,
      picker: this.#picker,
      storage: this.#storage,
      tracks: () => this.#tracks,
      onTrackChanged: (track) => {
        this.#layers.updateTrack(track)
        this.#liveHud?.refresh()
        this.#refreshTrackList()
        void this.#persistTracks()
      },
      // Résolu à l'appel : le contrôleur des marqueurs est construit juste
      // après, et aucun détour ne peut être créé avant.
      chooseKm: (track, candidates, onChosen) =>
        this.#markers.chooseKm(track, candidates, onChosen),
      wayOutSymbol: () => this.#wayOutSymbol(),
    })

    this.#markers = new MarkerController({
      map: this.#map,
      shell: this.#shell,
      translator: this.#translator,
      tracks: () => this.#tracks,
      onTrackChanged: (track) => {
        this.#layers.updateTrack(track)
        this.#liveHud?.refresh()
        this.#refreshTrackList()
        void this.#persistTracks()
      },
      // Résolu à l'appel : l'écran des réglages est construit après ce
      // contrôleur, mais aucune bulle ne peut s'ouvrir avant.
      onEditRealStart: (track) => this.#trackSettings.showKmSettings(track),
      symbolForType: (typeId) => this.#prefs.symbolForType(typeId),
      ways: {
        create: (track, exitKm, name) =>
          this.#ways.startCreation(track, exitKm, name),
        edit: (track, wayId, options) =>
          this.#ways.showEditDialog(track, wayId, options),
        moveExit: (track, wayId, exitKm) =>
          this.#ways.moveExit(track, wayId, exitKm),
        setVisible: (track, wayId, visible) =>
          this.#ways.setVisible(track, wayId, visible),
        delete: (track, wayId) => this.#ways.delete(track, wayId),
      },
    })

    this.#trackSettings = new TrackSettingsScreen(this.#translator, this.#prefs, {
      onTrackChanged: (track) => {
        this.#layers.updateTrack(track)
        this.#liveHud?.refresh()
        this.#refreshTrackList()
        void this.#persistTracks()
      },
      onRename: (track) =>
        promptDialog(document.body, {
          title: this.#translator.t('rename_track'),
          initialValue: track.name,
          okLabel: this.#translator.t('ok'),
          cancelLabel: this.#translator.t('cancel'),
          onSubmit: (value) => {
            if (value.length === 0) return
            track.name = value
            this.#refreshTrackList()
            void this.#persistTracks()
          },
        }),
      onPickColor: (track) => this.#showColorPicker(track.id),
      onShowMarkers: (track) => this.#markers.showList(track),
      onCheckRadars: (track) => void this.#radars.checkTrack(track),
      hasRadarData: () => this.#radarManager.hasLocalData(),
      onFetchElevation: (track) => void this.#elevation.fetch(track),
      needsElevation: (track) => this.#elevation.needsElevation(track),
      onToggleRoute: (track) => this.#route.toggle(track),
      sharesFileWithOthers: (track) => this.#sharesFileWithOthers(track),
      onFixPosition: () => this.#showFixPositionDialog(),
      canFixPosition: (track) => this.#fixPositionCandidates(track).length > 1,
      onToggleFileWaypoints: (track, include) =>
        this.#setFileWaypoints(track, include),
    })

    this.#wireMapEvents()

    this.#tracker = new SnappedPositionTracker(new LocalProgressionHintStore())
    this.#navigation = new NavigationController(
      () => this.#tracks,
      createMapRoadQuery(this.#map),
      // Reprise d'une sortie interrompue : la progression enregistrée lève
      // l'ambiguïté de boucle dès le premier accrochage, au lieu d'attendre
      // que le suivi la reconstitue.
      (trackId) => this.#tracker.hintFor(trackId),
    )
    this.#liveHud = new LiveHudController(
      this.#shell,
      this.#prefs,
      this.#navigation,
      this.#tracker,
      this.#translator,
    )

    this.#location = new LocationTracker({
      onUpdate: (sample) => this.#onLocation(sample),
      onError: (code) => {
        const key =
          code === 'permission_denied' ? 'location_denied' : 'location_unavailable'
        this.#shell.showSnackbar(this.#translator.t(key))

        // Un refus d'autorisation arrête la surveillance : le bouton doit
        // cesser d'annoncer un suivi qui n'a pas lieu.
        if (code === 'permission_denied') {
          this.#stopFollowing()
        }
      },
    })

    // Les panneaux sont configurés sans attendre le fond de carte. En zone mal
    // couverte, le chargement des tuiles peut prendre longtemps ou échouer :
    // la liste des traces et les réglages doivent rester accessibles.
    this.#applyPreferences()
    this.#updateScaleBar()

    // Exposée pour le diagnostic depuis la console du navigateur et pour le
    // harnais de captures, qui a besoin de convertir des coordonnées en
    // position à l'écran pour simuler des gestes.
    Object.defineProperty(window, 'velogpxMap', {
      value: this.#map,
      configurable: true,
    })

    /*
     * Avant le fond de carte, et non après : sur un poste où le style n'arrive
     * jamais, `#loadMapContent` s'arrête sur un délai dépassé, et un
     * avertissement posé derrière lui ne s'afficherait pas du tout.
     *
     * `start()` n'attend pas la réponse, et c'est délibéré : `main.ts` retire
     * l'écran de lancement à son retour, et l'avertissement resterait caché
     * derrière lui. Ce qui attend, c'est le **contenu** — traces restaurées et
     * suivi de position ne démarrent qu'une fois l'avertissement accepté. La
     * carte, elle, est déjà construite à ce stade : refuser arrête l'usage de
     * l'application, pas les requêtes de tuiles déjà parties.
     */
    void this.#acceptStartupWarning().then(() => this.#loadMapContent())
  }

  /**
   * Avertissement de lancement, jusqu'à son acceptation.
   *
   * Boucle plutôt qu'appel unique : refuser mène à un écran terminal d'où l'on
   * peut revenir au texte, et **un refus ne se retient pas**. Le retenir
   * enfermerait qui a cliqué à côté, sans rien pour en sortir — une page web
   * n'a pas de menu de réglages accessible avant son propre chargement.
   */
  async #acceptStartupWarning(): Promise<void> {
    while (!(await this.#askStartupWarning())) {
      await this.#showRefusalScreen()
    }
  }

  /**
   * Avertissement d'usage, à chaque chargement de la page.
   *
   * Deux choses en une : la sécurité — l'application ne doit pas détourner
   * l'attention de la route — et les termes de la licence, que l'écran « À
   * propos » affiche par ailleurs en permanence.
   *
   * **Il reparaît tant que la case n'est pas cochée**, là où Android retient
   * son acceptation une fois pour toutes. L'écart est délibéré et vient de la
   * nature des deux messages : celui d'Android est contractuel — on l'accepte,
   * c'est signé —, celui-ci porte en plus un rappel de sécurité, et un rappel
   * qu'on ne voit qu'une fois dans sa vie n'en est pas un. La case rend la
   * main à qui l'a lu.
   *
   * **Ce qui s'accepte est nommé.** Un bouton « Accepter » requalifie un
   * dialogue d'information en accord, et l'accord ne peut pas porter sur le
   * traitement des données : il n'y a rien à consentir — tout ce qui est
   * stocké ou transmis l'est pour exécuter le service demandé —, et un
   * consentement qu'on ne peut pas refuser sans perdre le service ne serait
   * pas libre au sens de l'article 7.4 du RGPD. L'acceptation porte donc sur
   * la sécurité et la licence, le paragraphe « Information » restant de
   * l'information, détaché et annoncé comme telle.
   *
   * Le dialogue n'est pas annulable : ni la touche d'échappement, ni le fond
   * ne le referment. Android ferme l'application sur un refus, ce qu'une page
   * web ne peut pas faire — `window.close()` ne vaut que pour une fenêtre
   * ouverte par script ; l'écran terminal en tient lieu.
   */
  #askStartupWarning(): Promise<boolean> {
    if (this.#prefs.get('startupWarningDismissed')) return Promise.resolve(true)

    const t = this.#translator
    const body = document.createElement('div')

    /*
     * Sécurité, puis licence : ce qui s'accepte, d'abord et ensemble.
     *
     * L'ordre n'est pas décoratif. La phrase de portée qui suit désigne « les
     * deux paragraphes ci-dessus » ; intercaler l'information entre eux
     * rendrait cette désignation fausse, et l'acceptation ambiguë sur ce
     * qu'elle couvre.
     */
    for (const key of ['startup_warning_safety', 'license_text'] as const) {
      const paragraph = document.createElement('p')
      paragraph.style.margin = '0 0 12px'
      paragraph.textContent = t.t(key)
      body.append(paragraph)
    }

    /*
     * Information, à part et annoncée.
     *
     * `startup_warning_storage` affirme qu'aucun cookie n'est déposé, ce qui
     * est vrai aujourd'hui — ni `document.cookie` côté front, ni `setcookie`
     * ni `session_start` côté PHP. **C'est une affirmation qui devient fausse
     * en silence** : le jour où un cookie apparaît, c'est ce texte qu'il faut
     * reprendre, et rien ne le rappellera. `tests/noCookies.test.ts` s'en
     * charge et refuse l'un sans l'autre.
     *
     * Il énumère aussi ce qui **sort** de l'appareil, coordonnées et adresse IP
     * comprises. Une version antérieure annonçait que rien n'était transmis à
     * un tiers, ce que `api/route.php` et `api/elevation.php` démentent à
     * chaque appel — sans parler des tuiles, qui viennent d'un hôte distant.
     */
    const info = document.createElement('section')
    info.className = 'startup-info'

    const infoTitle = document.createElement('h3')
    infoTitle.className = 'startup-info__title'
    infoTitle.textContent = t.t('startup_warning_info_title')

    const infoText = document.createElement('p')
    infoText.className = 'startup-info__text'
    infoText.textContent = t.t('startup_warning_storage')

    info.append(infoTitle, infoText)

    // Les deux documents dus sont lisibles sans quitter l'avertissement :
    // « À propos » les porte, mais il n'est pas atteignable tant que le
    // dialogue est ouvert, et l'information doit rester accessible au moment
    // où elle est donnée.
    const documents = document.createElement('div')
    documents.className = 'startup-info__links'
    documents.append(
      this.#legalLink('startup_warning_legal_link', 'about_legal_title', 'about_legal_text'),
      this.#legalLink('startup_warning_gdpr_link', 'about_gdpr_title', 'about_gdpr_text'),
    )
    info.append(documents)
    body.append(info)

    const scope = document.createElement('p')
    scope.className = 'startup-scope'
    scope.textContent = t.t('startup_warning_scope')
    body.append(scope)

    const label = document.createElement('label')
    label.className = 'dialog__checkbox'

    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'

    const text = document.createElement('span')
    text.textContent = t.t('startup_warning_dismiss')

    label.append(checkbox, text)
    body.append(label)

    return new Promise((resolve) => {
      showDialog(document.body, {
        title: t.t('startup_warning_title'),
        body,
        dismissible: false,
        readingHint: t.t('startup_warning_scroll_hint'),
        actions: [
          {
            label: t.t('startup_warning_refuse'),
            // La case ne se lit pas ici : « ne plus afficher » ne peut retenir
            // qu'une acceptation. Retenir un refus fermerait l'application
            // pour de bon, sans rien pour revenir dessus.
            onClick: () => resolve(false),
          },
          {
            label: t.t('startup_warning_accept'),
            // Aucun des deux n'est mis en avant : le refus doit se voir aussi
            // bien que l'acceptation, faute de quoi le choix n'en est pas un.
            readingRequired: true,
            // Lu au moment du clic, et non à chaque changement : cocher puis
            // décocher avant de valider doit laisser le message revenir.
            onClick: () => {
              if (checkbox.checked) this.#prefs.set('startupWarningDismissed', true)
              resolve(true)
            },
          },
        ],
      })
    })
  }

  /** Lien qui ouvre un document légal par-dessus l'avertissement. */
  #legalLink(
    linkKey: 'startup_warning_legal_link' | 'startup_warning_gdpr_link',
    titleKey: 'about_legal_title' | 'about_gdpr_title',
    textKey: 'about_legal_text' | 'about_gdpr_text',
  ): HTMLElement {
    const t = this.#translator
    const link = document.createElement('button')
    link.type = 'button'
    link.className = 'startup-info__link'
    link.textContent = t.t(linkKey)
    link.addEventListener('click', () => {
      showDialog(document.body, {
        title: t.t(titleKey),
        body: t.t(textKey, { ...PUBLISHER }),
        actions: [{ label: t.t('close') }],
        dismissible: true,
      })
    })
    return link
  }

  /**
   * Écran terminal d'un refus, tenant lieu du `finish()` d'Android.
   *
   * Ce n'est pas une page d'erreur, et surtout pas un code HTTP : refuser est
   * un choix, pas une panne, et l'annoncer comme telle ferait passer le site
   * pour cassé. Rien n'est enregistré, rien n'est chargé — la promesse ne se
   * résout que lorsque l'utilisateur demande à revoir le texte.
   */
  #showRefusalScreen(): Promise<void> {
    const t = this.#translator

    return new Promise((resolve) => {
      const screen = document.createElement('div')
      screen.className = 'refusal'

      const title = document.createElement('h1')
      title.className = 'refusal__title'
      title.textContent = t.t('startup_refused_title')

      const message = document.createElement('p')
      message.className = 'refusal__message'
      message.textContent = t.t('startup_refused_message')

      const back = document.createElement('button')
      back.type = 'button'
      back.className = 'refusal__back'
      back.textContent = t.t('startup_refused_back')
      back.addEventListener('click', () => {
        screen.remove()
        resolve()
      })

      screen.append(title, message, back)
      document.body.append(screen)
      back.focus()
    })
  }

  /**
   * Charge ce qui dépend du fond de carte.
   *
   * Un échec est signalé sans bloquer : les couches seront ajoutées à la
   * prochaine tentative de chargement du style.
   */
  async #loadMapContent(): Promise<void> {
    try {
      await this.#waitForStyle()
    } catch (error) {
      console.warn('[VeloGPX] Fond de carte indisponible', error)
      this.#shell.showSnackbar(this.#translator.t('offline'))
      return
    }

    await this.#restoreTracks()
    this.#applyPreferences()

    // Radars chargés après les traces : ils se posent au-dessus, et un
    // téléchargement lent ne doit pas retarder l'affichage du parcours.
    void this.#radars.load()

    // La position est suivie dès l'ouverture, comme sur Android : l'autorisation
    // est demandée tout de suite plutôt qu'au moment où l'utilisateur appuie sur
    // « ma position », et le point apparaît sans rien avoir à demander.
    this.#location.start()
  }

  #waitForStyle(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.#map.isStyleLoaded()) {
        resolve()
        return
      }

      this.#map.once('load', () => resolve())
      this.#map.once('error', (event) =>
        reject(event.error ?? new Error('Chargement du fond de carte impossible.')),
      )
    })
  }

  /* -------------------------------------------------------------------
   * Construction de l'interface
   * ---------------------------------------------------------------- */

  /**
   * Zoom visé par les boutons, tant que l'animation n'est pas terminée.
   *
   * `zoomIn()` part du zoom courant, qui pendant une animation vaut une valeur
   * intermédiaire : cinq clics rapides ne montaient que d'un niveau au lieu de
   * cinq. À la souris on clique bien plus vite qu'au doigt, ce qui rendait les
   * boutons quasiment inopérants sur ordinateur alors qu'ils semblaient
   * corrects sur téléphone. `MapManager.zoomIn` retient de même une cible.
   */
  #zoomTarget: number | null = null

  /**
   * Inclinaison courante et visée de la caméra.
   *
   * Comme le zoom, l'inclinaison ne peut pas être confiée à une animation de
   * MapLibre pendant le suivi : la boucle la remplacerait à l'image suivante.
   * C'est elle qui converge, au même rythme que la position.
   */
  #pitch = 0
  #pitchTarget = 0

  /** Taille du repère actuellement dessiné, pour détecter un changement. */
  #puckSize = 0

  /** Forme du repère actuellement dessiné : flèche en suivi, point sinon. */
  #puckShape: 'arrow' | 'dot' = 'dot'

  /** Dernier relevé reçu, pour redessiner le repère sans attendre le suivant. */
  #lastSample: LocationSample | null = null

  /** La grille du géoïde a-t-elle déjà été demandée ? */
  #geoidRequested = false

  /** Recentrage programmé après un geste, s'il y en a un. */
  #relockTimer: number | null = null

  /** Un doigt ou un bouton est posé sur la carte, caméra suspendue. */
  #pendingGesture = false

  /** Ce geste a-t-il réellement déplacé la carte, ou n'est-ce qu'un appui ? */
  #gestureMoved = false

  #zoomBy(delta: number): void {
    const map = this.#map
    const base = this.#zoomTarget ?? map.getZoom()
    const target = Math.min(
      Math.max(base + delta, map.getMinZoom()),
      map.getMaxZoom(),
    )

    // Quand la boucle d'affichage tourne, elle repositionne la caméra à chaque
    // image : elle interromprait l'animation dès l'image suivante, et les
    // boutons resteraient sans effet. Le zoom lui est donc confié.
    //
    // La condition porte sur la boucle et non sur `#following` : un geste
    // suspend la boucle en laissant le suivi actif, et pendant tout le délai
    // de recentrage les deux ne coïncident plus. Confier alors le zoom à une
    // boucle arrêtée revenait à l'avaler — les boutons ne répondaient plus
    // jusqu'au relock, qui appliquait le cumul d'un coup. `MapManager.zoomIn`
    // teste de même `cameraLoopRunning`.
    if (this.#cameraLoopRunning) {
      this.#zoomTarget = target
      return
    }

    // La cible est posée *après* l'animation : `easeTo` interrompt la
    // précédente, ce qui émet `zoomend` sur-le-champ et effacerait la valeur
    // qu'on vient d'écrire.
    map.easeTo({ zoom: target, duration: ZOOM_BUTTON_DURATION_MS })
    this.#zoomTarget = target
  }

  /**
   * Zoom à appliquer à l'image courante, ou `null` si aucun n'est demandé.
   *
   * Converge vers la cible plutôt que de l'appliquer d'un coup : un saut d'un
   * niveau entier serait brutal sur une carte qui défile.
   */
  #tickZoom(): number | null {
    if (this.#zoomTarget === null) return null

    const target = this.#zoomTarget
    const next =
      this.#map.getZoom() + (target - this.#map.getZoom()) * ZOOM_EASE_ALPHA

    if (Math.abs(target - next) < ZOOM_SNAP) {
      this.#zoomTarget = null
      return target
    }

    return next
  }

  #buildUi(): void {
    this.#shell = new Shell(this.overlayElement, this.#translator, {
      onMenu: () => this.#drawer.toggle(),
      onMyLocation: () => this.#toggleFollow(),
      onGuidance: () => this.#toggleGuidance(),
      onSatellite: () => this.#toggleSatellite(),
      onZoomIn: () => this.#zoomBy(1),
      onZoomOut: () => this.#zoomBy(-1),
      onCompass: () => this.#map.easeTo({ bearing: 0, pitch: 0, duration: 300 }),
      onKmIndicator: () => this.#showFixPositionDialog(),
    })

    this.#drawer = new Drawer(
      this.overlayElement,
      this.#translator,
      {
        onOpenFile: () => this.#pickFiles(),
        onOpenSettings: () => {
          this.#drawer.close()
          this.#settings.open()
        },
        onSelectTrack: (id) => {
          const track = this.#tracks.find((t) => t.id === id)
          if (track) this.#fitTrack(track)
        },
        onTrackMenu: (id, anchor) => this.#showTrackMenu(id, anchor),
        onColorTap: (id) => this.#showColorPicker(id),
        onReorder: (order) => this.#reorderTracks(order),
        onCancelRouting: (id) => {
          const track = this.#tracks.find((t) => t.id === id)
          // Pas de message : le calcul disparaît de la ligne, ce que
          // l'utilisateur vient de demander et voit aussitôt. Android n'en
          // affiche pas davantage.
          if (track) this.#route.cancel(track)
        },
      },
    )

    this.#settings = new SettingsScreen(
      this.overlayElement,
      this.#translator,
      this.#prefs,
      {
        // Attendu, sans quoi l'écran relit l'espace occupé avant que la purge
        // ait eu lieu et affiche le même chiffre qu'avant — le bouton passait
        // pour sans effet alors qu'il avait travaillé.
        onClearCache: async () => {
          await this.#storage.clearCache()
          this.#shell.showSnackbar(this.#translator.t('about_cache_cleared'))
        },
        storageUsage: () => this.#storage.estimateUsage(),
        cacheCounts: () => this.#storage.cacheCounts(),
        onPreferencesChanged: () => this.#applyPreferences(),
        showHudPreview: (kind) => this.#liveHud.showPreview(kind),
        confirmRadars: () => this.#radars.confirmEnable(),
        refreshRadars: async ({ onProgress }) => {
          const t = this.#translator
          const countries = this.#prefs.get('radarCountries')

          // La sélection est appliquée d'abord : elle supprime les caches des
          // pays retirés, sans quoi ils resteraient affichés.
          // Deux intentions derrière le même bouton : rattraper ce qui manque
          // — pays ajouté, ou téléchargement en échec plus tôt dans la session
          // — ou, si rien ne manque, rafraîchir volontairement l'ensemble.
          // Tout retélécharger dans le premier cas coûterait plusieurs minutes
          // pour rien.
          if (this.#radarManager.missingCountries(countries).length === 0) {
            this.#radarManager.forgetAll()
          }

          const result = await this.#radarManager.applyCountrySelection(
            countries,
            (progress) => {
              const country =
                new Intl.DisplayNames([t.locale], { type: 'region' }).of(
                  progress.country,
                ) ?? progress.country

              onProgress(
                progress.stage === 'processing'
                  ? t.t('radars_processing')
                  : progress.bytes > 0
                    ? t.t('radars_refreshing_country_progress', {
                        country,
                        size: t.fileSize(progress.bytes),
                      })
                    : t.t('radars_refreshing_country', { country }),
              )
            },
          )

          if (result.failed.length === 0) {
            this.#prefs.set('radarsLastRefresh', Date.now())
          }

          this.#applyPreferences()
          return { count: result.radars.length, failed: result.failed }
        },
      },
      APP_VERSION,
    )
  }

  #wireMapEvents(): void {
    // Un seul écouteur pour la boussole et l'échelle : les deux dépendent de
    // la même position de caméra, les séparer doublerait les lectures.
    this.#map.on('move', () => {
      this.#shell.setCompassBearing(this.#map.getBearing())
      this.#updateScaleBar()
    })

    // La caméra est suspendue dès que le doigt se pose, et non au premier
    // déplacement : la boucle repositionne la carte à chaque image, ce qui
    // annule le geste avant que MapLibre ne le reconnaisse. Le `dragstart`
    // attendu n'arrivait jamais, et la carte restait impossible à déplacer.
    const beginGesture = (): void => {
      if (!this.#following || !this.#cameraLoopRunning) return

      this.#stopCameraLoop()
      this.#pendingGesture = true
      this.#gestureMoved = false
    }

    const markMoved = (): void => {
      if (this.#pendingGesture) this.#gestureMoved = true
    }

    const endGesture = (): void => {
      if (!this.#pendingGesture) return
      this.#pendingGesture = false

      // Un simple appui — ouvrir un marqueur, par exemple — ne doit pas
      // suspendre le recentrage : la caméra reprend aussitôt. Seul un
      // déplacement réel laisse la main à l'utilisateur.
      if (this.#gestureMoved) this.#scheduleRelock()
      else if (this.#following) this.#startCameraLoop()
    }

    this.#map.on('mousedown', beginGesture)
    this.#map.on('touchstart', beginGesture)
    this.#map.on('dragstart', markMoved)
    this.#map.on('rotatestart', markMoved)
    this.#map.on('pitchstart', markMoved)

    // La fin du geste est écoutée des deux côtés : MapLibre pour le cas
    // ordinaire, la fenêtre pour un relâchement hors de la carte, qui
    // laisserait sinon la caméra suspendue indéfiniment. `endGesture` ne fait
    // rien s'il est appelé deux fois.
    this.#map.on('mouseup', endGesture)
    this.#map.on('touchend', endGesture)
    this.#map.on('touchcancel', endGesture)
    window.addEventListener('pointerup', endGesture)
    window.addEventListener('pointercancel', endGesture)

    // La cible des boutons de zoom n'est oubliée qu'une fois atteinte : un
    // `zoomend` émis par l'interruption d'une animation précédente ne doit pas
    // annuler le cumul des clics en cours.
    this.#map.on('zoomend', () => {
      if (
        this.#zoomTarget !== null &&
        Math.abs(this.#map.getZoom() - this.#zoomTarget) < 0.001
      ) {
        this.#zoomTarget = null
      }
    })

    // La molette, le pincement ou un double-clic reprennent la main : la cible
    // accumulée n'a plus de rapport avec ce que voit l'utilisateur.
    this.#map.on('zoomstart', (event) => {
      // `originalEvent` distingue le geste de l'utilisateur des mouvements que
      // la boucle caméra provoque elle-même.
      if (!('originalEvent' in event) || !event.originalEvent) return

      this.#zoomTarget = null

      // Un zoom à la molette n'a ni appui ni relâchement : il suspend et
      // programme le recentrage d'un seul tenant.
      if (this.#pendingGesture) this.#gestureMoved = true
      else this.#onUserGesture()
    })

    this.#map.on('click', (event) => {
      // Une sélection de point en cours consomme le clic — création d'un
      // détour, choix d'un point de passage — avant tout autre traitement.
      if (this.#picker.handleMapClick(event.lngLat.lat, event.lngLat.lng)) {
        return
      }

      // Un déplacement de marqueur en cours consomme le clic : c'est lui qui
      // désigne la nouvelle position.
      if (this.#markers.handleMapClick(event.lngLat.lat, event.lngLat.lng)) {
        return
      }

      this.#markers.closePopup()

      const layers = this.#layers.clickableLayerIds()
      if (layers.length === 0) return

      // Cible élargie de quelques pixels : viser une pastille au doigt en
      // roulant demande de la tolérance.
      const tolerance = MarkerController.clickTolerancePx
      const features = this.#map.queryRenderedFeatures(
        [
          [event.point.x - tolerance, event.point.y - tolerance],
          [event.point.x + tolerance, event.point.y + tolerance],
        ],
        { layers },
      )

      // Le plus proche du doigt, et non le premier venu : la boîte de
      // tolérance attrape tout ce qui l'entoure, et à faible zoom cela peut
      // être la trace entière. L'intention est le symbole visé.
      const feature = nearestFeature(features, event.point, this.#map)
      if (!feature?.properties) return

      const trackId = feature.properties.track_id
      const markerIndex = feature.properties.marker_index

      // Point de passage d'un détour : le déplacer ou le retirer. Ces entités
      // n'ont pas de titre, elles ne mèneraient à aucune étiquette.
      const wayId = feature.properties.way_id
      const viaIndex = feature.properties.via_index
      if (
        typeof trackId === 'string' &&
        typeof wayId === 'string' &&
        typeof viaIndex === 'number'
      ) {
        const track = this.#tracks.find((t) => t.id === trackId)
        if (track) {
          this.#ways.showViaActions(track, wayId, viaIndex)
          return
        }
      }

      // Marqueur personnalisé : bulle complète, avec ses trois actions.
      if (typeof trackId === 'string' && typeof markerIndex === 'number') {
        const track = this.#tracks.find((t) => t.id === trackId)
        const marker = track?.customMarkers[markerIndex]

        if (track && marker) {
          this.#markers.showPopup(track, marker, {
            lat: event.lngLat.lat,
            lon: event.lngLat.lng,
          })
          return
        }
      }

      // Départ, départ fictif et départ réel : même bulle à trois boutons que
      // les marqueurs, ciblant le départ réel. L'arrivée en est exclue, elle ne
      // commande rien.
      const role = feature.properties.marker
      if (
        typeof trackId === 'string' &&
        (role === 'start' ||
          role === 'fictive-start' ||
          role === 'start-end' ||
          role === 'real-start')
      ) {
        const track = this.#tracks.find((t) => t.id === trackId)
        if (track) {
          this.#markers.showStartPopup(track, {
            lat: event.lngLat.lat,
            lon: event.lngLat.lng,
          })
          return
        }
      }

      // Radar : les entités n'ont ni titre ni description, l'étiquette se
      // compose de son type et de sa vitesse quand elle est connue.
      const radarId = feature.properties.radar_id
      if (typeof radarId === 'string') {
        const speed = feature.properties.speed_limit
        const label =
          typeof speed === 'number' && speed > 0
            ? this.#translator.t('radar_popup_title', {
                type: this.#translator.t('marker_type_radar'),
                speed,
              })
            : this.#translator.t('marker_type_radar')

        this.#markers.showLabelPopup(label, '', {
          lat: event.lngLat.lat,
          lon: event.lngLat.lng,
        })
        return
      }

      // Point d'intérêt et arrivée : simple étiquette.
      const title = String(feature.properties.title ?? '')
      const description = String(feature.properties.description ?? '')
      if (title === '' && description === '') return

      this.#markers.showLabelPopup(title, description, {
        lat: event.lngLat.lat,
        lon: event.lngLat.lng,
      })
    })

    // Les points de passage des détours se tirent directement à la souris ou
    // au doigt ; le menu en deux temps reste disponible au tap simple.
    enableWayViaDrag({
      map: this.#map,
      tracks: () => this.#tracks,
      onPreview: (track) => this.#layers.updateWayVias(track),
      onCommit: (track, wayId, viaIndex, position) =>
        this.#ways.moveVia(track, wayId, viaIndex, position),
      isBusy: () => this.#picker.isPicking || this.#markers.isMoving,
    })

    // Appui long : ajout d'un marqueur sur la trace touchée. Neutralisé
    // pendant une sélection de point, où le même geste servirait deux
    // intentions contradictoires.
    onMapLongPress(this.#map, ({ lat, lon }) => {
      if (this.#picker.isPicking) return
      this.#markers.handleLongPress(lat, lon)
    })

    // MapLibre suit les redimensionnements de fenêtre, mais pas ceux de son
    // conteneur : bascule portrait/paysage, barre d'URL mobile qui se replie,
    // ouverture des outils de développement. Sans cet observateur, la carte
    // garde la taille qu'elle avait au premier rendu.
    // Le profil d'altitude se redessine à la largeur que ses voisins lui
    // laissent : une rotation la change du tout au tout, et attendre le tick
    // suivant laisserait jusqu'à cinq secondes de courbe à l'ancienne échelle.
    const onResize = (): void => {
      this.#map.resize()
      this.#updateScaleBar()
      this.#liveHud.refresh()
    }

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(onResize)
      observer.observe(this.mapElement)
    } else {
      window.addEventListener('resize', onResize)
    }

    this.#wireFileDrop()
  }

  /**
   * Import par glisser-déposer.
   *
   * Équivalent web de la réception d'un fichier par intent sur Android : sur
   * ordinateur, déposer un GPX sur la carte est le geste attendu.
   */
  #wireFileDrop(): void {
    const target = this.appElement

    target.addEventListener('dragover', (event) => {
      // Sans `preventDefault`, le navigateur ouvre le fichier à la place.
      event.preventDefault()
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
    })

    target.addEventListener('drop', (event) => {
      event.preventDefault()

      const files = Array.from(event.dataTransfer?.files ?? [])
      if (files.length > 0) {
        // Même `catch` que le champ de fichier, et pour la même raison : ce
        // qui échappe au `try` par fichier — persistance, routage proposé,
        // cadrage — deviendrait un rejet non traité, que rien n'affiche.
        // Le chemin du glisser-déposer était resté sans lui.
        void this.#importFiles(files).catch((error: unknown) => {
          console.error('[VeloGPX] Import interrompu', error)
          this.#shell.showSnackbar(
            this.#translator.t('error_import_failed', {
              reason: error instanceof Error ? error.message : String(error),
            }),
          )
        })
      }
    })
  }

  #updateScaleBar(): void {
    const center = this.#map.getCenter()
    this.#shell.setScale(metersPerPixel(this.#map.getZoom(), center.lat))
  }

  /* -------------------------------------------------------------------
   * Application des réglages
   * ---------------------------------------------------------------- */

  #applyTheme(): void {
    const theme = this.#prefs.get('theme')
    const root = document.documentElement

    if (theme === 'system') {
      delete root.dataset.theme
    } else {
      root.dataset.theme = theme
    }
  }

  #applyPreferences(): void {
    this.#applyTheme()
    this.#translator.setLocale(resolveLocale(this.#prefs.get('language')))

    // La taille du repère n'est appliquée que par la boucle caméra. En veille,
    // rien ne bouge et le nouveau réglage attendrait le prochain déplacement :
    // remettre le compteur à zéro la réveille pour au moins une image.
    this.#idleFrames = 0

    const prefs = this.#prefs.values

    this.#shell.zoomButtons.hidden = !prefs.showZoomButtons
    this.#shell.fabGuidance.hidden = !prefs.showGuidanceButton
    this.#shell.compass.dataset.belowGuidance = String(prefs.showGuidanceButton)

    // Le panneau est réaffiché depuis le dernier relevé plutôt que vidé : un
    // changement de réglage ne doit pas effacer vitesse et altitude en
    // attendant le relevé suivant, qui ne vient plus à l'arrêt.
    this.#refreshSpeedAltitude()

    // Les couches n'existent pas tant que le style n'est pas chargé ; les
    // interroger avant lèverait une erreur. Un échange en cours revient au
    // même : le style qui s'en va est en train d'être démonté, et tout sera
    // reposé sur le nouveau à la fin de l'échange.
    if (!this.#map.isStyleLoaded() || this.#styleSwapping) {
      return
    }

    // Le fond de carte a changé : tout est reposé après le chargement du
    // nouveau style, il n'y a rien à appliquer sur celui qui s'en va.
    if (prefs.mapStyle !== this.#appliedStyle) {
      void this.#changeMapStyle(prefs.mapStyle)
      return
    }

    const style = getStyleDescriptor(prefs.mapStyle)
    this.#layers.setBuildings3d(prefs.show3dBuildings && style.supports3dBuildings)
    this.#layers.setMarkerScale(prefs.markerScale)
    this.#layers.setSatellite(prefs.satelliteEnabled, prefs.mapStyle)
    this.#layers.setRadars(this.#radarManager.radars, prefs.radarsEnabled)
    this.#shell.fabSatellite.setAttribute(
      'aria-pressed',
      String(prefs.satelliteEnabled),
    )

    // Les pastilles kilométriques et les marqueurs sont recalculés : leur
    // nombre et leur taille dépendent directement des réglages.
    for (const track of this.#tracks) {
      this.#layers.updateTrack(track)
    }
  }

  /**
   * Remplace le fond de carte.
   *
   * `setStyle` détruit toutes les sources, couches et images du style
   * précédent, les nôtres comprises : sans cette repose, choisir un style dans
   * les réglages effaçait les traces, les marqueurs et les radars. Avant cette
   * méthode, le réglage n'avait tout simplement aucun effet — le style n'était
   * lu qu'à la construction de la carte, et il fallait recharger la page.
   */
  async #changeMapStyle(styleId: MapStyleId): Promise<void> {
    if (this.#styleSwapping) return

    const previous = this.#appliedStyle
    this.#styleSwapping = true
    this.#appliedStyle = styleId

    try {
      this.#map.setStyle(getStyleUrl(styleId))
      await this.#waitForStyleSwap()
    } catch (error) {
      // La demande est oubliée pour que le prochain passage la retente : sans
      // cela, un style resté en travers deviendrait définitif jusqu'au
      // rechargement.
      this.#appliedStyle = previous
      console.warn('[VeloGPX] Changement de fond de carte impossible', error)
      this.#shell.showSnackbar(this.#translator.t('offline'))
      return
    } finally {
      this.#styleSwapping = false
    }

    // Les images de marqueurs appartiennent au style : le registre doit les
    // oublier, sinon elles seraient réputées posées alors qu'elles ont disparu.
    this.#layers.resetImageCache()

    // Le repère de position est un marqueur MapLibre, pas une couche : il
    // survit au changement de style et n'a rien à reconstruire.
    this.#applyPreferences()
    this.#drawPassivePosition()
  }

  /**
   * Attend qu'un style de remplacement soit posé.
   *
   * Distinct de `#waitForStyle` : l'événement `load` de MapLibre ne part qu'une
   * fois dans la vie de la carte, il ne dit rien d'un style échangé ensuite.
   * Le délai est un garde-fou pour la zone blanche, où le style peut ne jamais
   * arriver.
   *
   * **`styledata` ne suffit pas**, et c'est ce qui faisait perdre la trace à
   * chaque changement de fond, par intermittence. Mesuré sur « Colorful » : les
   * trois `styledata` partent à 462, 628 et 630 ms, **tous avec
   * `isStyleLoaded()` encore faux**, et plus aucun ne suit. Le seul événement
   * qui arrive style chargé est `idle`, à 1523 ms. N'écouter que `styledata`
   * laissait donc l'attente expirer au bout de vingt secondes, `#changeMapStyle`
   * tomber dans son `catch`, et les couches — traces, marqueurs, radars — ne
   * jamais être reposées. Les deux événements sont écoutés : `styledata` rend
   * la main plus tôt quand il tombe du bon côté, `idle` garantit qu'on y arrive.
   */
  #waitForStyleSwap(timeoutMs = 20_000): Promise<void> {
    return new Promise((resolve, reject) => {
      const onStyleReady = (): void => {
        // Le contrôle reste sur les deux : reposer les couches sur un style
        // encore incomplet les verrait détruites juste après.
        if (!this.#map.isStyleLoaded()) return
        cleanup()
        resolve()
      }

      const timer = window.setTimeout(() => {
        cleanup()
        reject(new Error('Fond de carte non chargé.'))
      }, timeoutMs)

      const cleanup = (): void => {
        window.clearTimeout(timer)
        this.#map.off('styledata', onStyleReady)
        this.#map.off('idle', onStyleReady)
      }

      this.#map.on('styledata', onStyleReady)
      this.#map.on('idle', onStyleReady)
    })
  }

  #toggleSatellite(): void {
    const enabled = !this.#prefs.get('satelliteEnabled')
    this.#prefs.set('satelliteEnabled', enabled)
    this.#layers.setSatellite(enabled, this.#prefs.get('mapStyle'))
    this.#shell.fabSatellite.setAttribute('aria-pressed', String(enabled))
  }

  /* -------------------------------------------------------------------
   * Position
   * ---------------------------------------------------------------- */

  #toggleFollow(): void {
    if (this.#following) {
      this.#stopFollowing()
      return
    }

    this.#following = true
    this.#shell.fabMyLocation.setAttribute('aria-pressed', 'true')

    // Chargement anticipé de la grille du géoïde : deux mégaoctets, à demander
    // dès l'activation du suivi pour que l'altitude soit juste au premier
    // relevé plutôt qu'au bout de quelques secondes.
    this.#ensureGeoid()

    this.#recenterOnLocation()

    this.#liveHud.start()

    // Le cercle de précision disparaît en suivi : la carte y est déjà centrée
    // sur la position, et l'incertitude n'apporte rien pendant la navigation.
    // C'est la distinction que fait Android entre ses deux modes de rendu.
    this.#layers.setAccuracyCircle(null)

    // L'écran reste allumé tant que le suivi est actif, et seulement alors :
    // le maintenir au repos viderait la batterie sans rien apporter.
    void this.#wakeLock.acquire()
  }

  /**
   * Recentre la carte sur la position et (re)démarre la boucle caméra.
   *
   * Sert à l'activation du suivi comme au recentrage automatique après un
   * geste : les deux doivent poser la marge sans faire sauter l'affichage.
   */
  #recenterOnLocation(): void {
    this.#cancelRelock()

    // Réserver la marge haute déplacerait d'un coup ce qui est affiché. On
    // relève donc le point qui occupe déjà la hauteur visée, et on recentre
    // dessus en posant la marge : à l'écran, rien ne bouge.
    const canvas = this.#map.getCanvas()
    const anchor = this.#map.unproject([
      canvas.clientWidth / 2,
      canvas.clientHeight * FOLLOW_POSITION_RATIO,
    ])

    this.#map.jumpTo({
      center: anchor,
      padding: {
        top: canvas.clientHeight * FOLLOW_TOP_PADDING_RATIO,
        bottom: 0,
        left: 0,
        right: 0,
      },
    })

    // Ce même point sert de départ : la position réelle est rejointe par
    // glissement, comme tout le reste, au lieu d'apparaître d'un bond.
    this.#navigation.seedDisplayPosition(
      anchor.lat,
      anchor.lng,
      this.#map.getBearing(),
    )

    // Le cap, lui, est repris à chaque prise de suivi et pas seulement avant
    // le premier relevé : la boucle l'impose maintenant à la carte, et partirait
    // sinon d'une valeur périmée — d'avant le geste, ou du tout premier relevé.
    this.#navigation.seedDisplayBearing(this.#map.getBearing())

    // Le dernier relevé est rejoué : sans lui, la carte attendrait le relevé
    // suivant pour se recentrer — qui peut ne jamais venir, le navigateur
    // cessant d'en émettre quand la position ne bouge plus.
    if (this.#lastSample !== null) {
      this.#navigation.onLocation(this.#lastSample)
    }

    this.#startCameraLoop()
  }

  /**
   * Geste de l'utilisateur pendant le suivi.
   *
   * La boucle caméra est **mise en pause**, pas arrêtée : le suivi reste
   * actif, les panneaux de progression continuent, et seule la caméra rend la
   * main. Sans cette pause, la boucle repositionnerait la carte à chaque image
   * et tout déplacement serait impossible.
   *
   * Le recentrage revient de lui-même après le délai réglé — regarder autour
   * de soi ne doit pas obliger à réactiver le suivi.
   */
  #onUserGesture(): void {
    if (!this.#following) return

    this.#stopCameraLoop()
    this.#scheduleRelock()
  }

  /**
   * Cadre la carte sur une trace, sans se faire écraser par la boucle.
   *
   * Un cadrage pendant le suivi est traité **comme un geste** : la boucle
   * caméra repose `center`, `zoom`, `pitch` et `bearing` à chaque image, et
   * elle avalait l'animation entière — choisir une trace dans le tiroir, ou
   * finir un import, ne montrait rien. Android fait de même, et son
   * commentaire dit l'autre raison : sans l'arrêt, la boucle ré-émettrait son
   * zoom périmé, resynchronisé seulement à ses reprises
   * (`MapManager.zoomToTrack`).
   */
  #fitTrack(track: GpxTrack): void {
    if (this.#cameraLoopRunning) {
      this.#stopCameraLoop()
      this.#scheduleRelock()
    }

    this.#layers.fitTrack(track)
  }

  #scheduleRelock(): void {
    this.#cancelRelock()

    const delayMs = this.#prefs.get('relockDelay') * 1000
    this.#relockTimer = window.setTimeout(() => {
      this.#relockTimer = null
      if (this.#following) this.#recenterOnLocation()
    }, delayMs)
  }

  #cancelRelock(): void {
    if (this.#relockTimer === null) return
    window.clearTimeout(this.#relockTimer)
    this.#relockTimer = null
  }

  #stopFollowing(): void {
    if (!this.#following) return

    this.#following = false
    this.#guidance = false
    this.#shell.fabMyLocation.setAttribute('aria-pressed', 'false')
    this.#shell.fabGuidance.setAttribute('aria-pressed', 'false')

    void this.#wakeLock.release()
    this.#cancelRelock()

    this.#stopCameraLoop()
    this.#liveHud.stop()
    this.#navigation.reset()

    // La position continue d'être suivie et affichée : seul le recentrage
    // s'arrête. Le repère est redessiné sur-le-champ à partir du dernier
    // relevé — l'attendre laisserait la carte sans repère, parfois longtemps :
    // à l'arrêt, le navigateur n'émet plus rien.
    this.#puck?.remove()
    this.#puck = null
    this.#drawPassivePosition()

    this.#pitch = 0
    this.#pitchTarget = 0
    this.#zoomTarget = null

    // Retirer la marge déplacerait le contenu affiché : on recentre d'abord
    // sur ce que l'utilisateur a sous les yeux, pour que la sortie du suivi ne
    // fasse pas sauter la carte.
    const canvas = this.#map.getCanvas()
    const visibleCenter = this.#map.unproject([
      canvas.clientWidth / 2,
      canvas.clientHeight / 2,
    ])

    this.#map.jumpTo({
      center: visibleCenter,
      pitch: 0,
      padding: { top: 0, bottom: 0, left: 0, right: 0 },
    })
  }

  #toggleGuidance(): void {
    // Le guidage suppose le suivi : l'activer seul n'aurait pas de sens.
    if (!this.#following) {
      this.#toggleFollow()
    }

    this.#guidance = !this.#guidance
    this.#shell.fabGuidance.setAttribute('aria-pressed', String(this.#guidance))

    // Cibles confiées à la boucle caméra, qui pilote seule la vue pendant le
    // suivi. Sortir du guidage redresse la vue sans toucher au zoom : celui-ci
    // a pu être ajusté entre-temps, le remettre d'office serait une surprise.
    this.#pitchTarget = this.#guidance ? GUIDANCE_TILT : 0
    if (this.#guidance) {
      this.#zoomTarget = Math.min(
        Math.max(GUIDANCE_ZOOM, this.#map.getMinZoom()),
        this.#map.getMaxZoom(),
      )
    }
  }

  /** Inclinaison de l'image courante, convergeant vers sa cible. */
  #tickPitch(): number {
    this.#pitch += (this.#pitchTarget - this.#pitch) * ZOOM_EASE_ALPHA
    if (Math.abs(this.#pitchTarget - this.#pitch) < PITCH_SNAP) {
      this.#pitch = this.#pitchTarget
    }
    return this.#pitch
  }

  /**
   * Boucle d'affichage de la position.
   *
   * Le déplacement est recalculé à chaque image plutôt que confié aux
   * animations de la carte : entre deux relevés GPS, la position est
   * extrapolée le long de la trace, ce qu'aucune interpolation entre deux
   * points fixes ne saurait reproduire.
   */
  #startCameraLoop(): void {
    if (this.#cameraLoopRunning) return
    this.#cameraLoopRunning = true
    this.#idleFrames = 0

    /**
     * En veille, `requestAnimationFrame` réveillerait la boucle soixante fois
     * par seconde quand même : c'est le minuteur qui espace réellement les
     * images, l'appel à `requestAnimationFrame` ne servant plus qu'à rester
     * synchrone avec le rendu.
     */
    const schedule = (idle: boolean): void => {
      if (!idle) {
        this.#frameHandle = requestAnimationFrame(frame)
        return
      }
      this.#idleTimer = window.setTimeout(() => {
        this.#idleTimer = null
        this.#frameHandle = requestAnimationFrame(frame)
      }, IDLE_FRAME_INTERVAL_MS)
    }

    const frame = (): void => {
      this.#frameHandle = null
      if (!this.#following) {
        this.#cameraLoopRunning = false
        return
      }

      const state = this.#navigation.tick()
      const zoom = this.#tickZoom()
      const pitch = this.#tickPitch()

      // La veille exige que les quatre grandeurs soient arrivées : position et
      // cap sont menés par `navigation`, zoom et inclinaison ici même.
      const settled =
        state.converged && zoom === null && pitch === this.#pitchTarget

      this.#idleFrames = settled ? this.#idleFrames + 1 : 0
      const idle = settled && this.#idleFrames > IDLE_FRAME_THRESHOLD

      if (!idle) {
        if (this.#navigation.hasFix) {
          this.#updatePuck(state.lat, state.lon, state.bearing)

          this.#map.jumpTo({
            center: [state.lon, state.lat],
            ...(zoom === null ? {} : { zoom }),
            pitch,
            // La carte s'oriente selon la direction dès le suivi simple, comme
            // l'application Android, où le guidage n'ajoute que l'inclinaison
            // et le zoom rapproché. Le gel à l'arrêt est déjà assuré en amont :
            // `navigation` ne fait tourner le cap qu'au-dessus du seuil de
            // vitesse, sans quoi la carte pivoterait sur le bruit du GPS.
            bearing: state.bearing,
            // Marge réservée dès le suivi simple, et pas seulement en guidage.
            padding: {
              top: this.#map.getCanvas().clientHeight * FOLLOW_TOP_PADDING_RATIO,
              bottom: 0,
              left: 0,
              right: 0,
            },
          })
        } else if (zoom !== null) {
          // Suivi actif mais sans position : la boucle ne recentre rien, elle
          // doit tout de même appliquer le zoom demandé.
          this.#map.jumpTo({ zoom })
        }
      }

      schedule(idle)
    }

    schedule(false)
  }

  #stopCameraLoop(): void {
    this.#cameraLoopRunning = false
    this.#idleFrames = 0

    if (this.#frameHandle !== null) {
      cancelAnimationFrame(this.#frameHandle)
      this.#frameHandle = null
    }
    if (this.#idleTimer !== null) {
      clearTimeout(this.#idleTimer)
      this.#idleTimer = null
    }
  }

  /**
   * Repère de position.
   *
   * Flèche orientée pendant le suivi, simple point le reste du temps — les
   * deux modes de rendu de l'application Android. Hors suivi, une flèche
   * pointerait dans une direction arbitraire : à l'arrêt, le cap n'a pas de
   * sens.
   */
  #updatePuck(lat: number, lon: number, bearing: number): void {
    const shape = this.#following ? 'arrow' : 'dot'
    const base = locationIconSize(this.#prefs.get('locationIconSize'))
    const size = Math.round(
      base * (shape === 'arrow' ? FOLLOW_ICON_RATIO : IDLE_ICON_RATIO),
    )

    // Le repère est refait si sa taille ou sa forme a changé : le réglage se
    // modifie en cours de suivi, et attendre l'arrêt pour en voir l'effet
    // donnerait l'impression d'un curseur sans effet.
    if (
      this.#puck !== null &&
      (this.#puckSize !== size || this.#puckShape !== shape)
    ) {
      this.#puck.remove()
      this.#puck = null
    }

    if (this.#puck === null) {
      const element = document.createElement('div')
      element.innerHTML =
        shape === 'arrow' ? navigationArrowMarkup(size) : positionDotMarkup(size)
      this.#puckSize = size
      this.#puckShape = shape
      element.style.willChange = 'transform'
      // Sans cela, le conteneur mesure quelques pixels de plus que la flèche —
      // l'espace réservé sous la ligne de base d'un élément en ligne. MapLibre
      // centre le conteneur, la flèche se retrouverait décalée vers le haut.
      element.style.lineHeight = '0'

      this.#puck = new maplibregl.Marker({ element, rotationAlignment: 'map' })
        .setLngLat([lon, lat])
        .addTo(this.#map)
    }

    this.#puck.setLngLat([lon, lat])
    this.#puck.setRotation(shape === 'arrow' ? bearing : 0)
  }

  #onLocation(sample: LocationSample): void {
    this.#lastSample = sample

    // La grille du géoïde pèse deux mégaoctets : elle n'est demandée qu'au
    // premier relevé qui porte une altitude, et le panneau est réaffiché dès
    // qu'elle arrive.
    if (sample.altitude !== null) this.#ensureGeoid()

    this.#refreshSpeedAltitude()

    // Un relevé trop imprécis alimente le HUD vitesse et altitude, puis s'en
    // tient là : ni cible de caméra, ni accrochage, ni repère déplacé. C'est
    // l'ordre d'Android (`MapManager.kt:1828-1836`), et la raison est qu'un
    // point à 200 m sous couvert urbain ferait sauter la carte et repartir le
    // calcul d'accrochage. Le moteur écartait déjà ce relevé pour son propre
    // compte ; c'est ici qu'il était repris comme les autres.
    if (!sample.trackable) return

    this.#navigation.onLocation(sample)

    // Hors suivi, ou pendant une pause de la caméra, aucune boucle ne tourne :
    // c'est le relevé lui-même qui déplace le repère.
    if (!this.#following) {
      this.#drawPassivePosition()
    } else if (!this.#cameraLoopRunning) {
      // Pause après un geste : le repère continue de suivre la position, seule
      // la caméra reste où l'utilisateur l'a laissée.
      this.#updatePuck(sample.lat, sample.lon, sample.bearing ?? 0)
    }
  }

  /**
   * Réaffiche le panneau vitesse / altitude depuis le dernier relevé.
   *
   * Séparé du traitement d'un relevé parce qu'il faut pouvoir le rejouer sans
   * en attendre un nouveau : c'est ce qui manquait à l'arrivée de la grille du
   * géoïde.
   */
  #refreshSpeedAltitude(): void {
    const sample = this.#lastSample
    const prefs = this.#prefs.values

    // Sans relevé, le panneau n'affiche rien mais prend quand même sa taille et
    // sa visibilité : c'est ce qui l'initialise au démarrage.
    if (sample === null) {
      this.#shell.setSpeedAltitude(null, null, {
        visible: prefs.showSpeedAltitude,
        showAltitude: prefs.showAltitude,
        textSizeLevel: prefs.speedAltitudeSize,
      })
      return
    }

    // Le navigateur donne une altitude ellipsoïdale ; celle qu'attend un
    // cycliste est comptée depuis le niveau des mers, soit environ 45 m de
    // moins en France. Tant que la grille n'est pas chargée, l'altitude brute
    // est affichée plutôt que rien.
    const altitude =
      sample.altitude === null
        ? null
        : this.#geoid.toMsl(sample.lat, sample.lon, sample.altitude)

    this.#shell.setSpeedAltitude(
      sample.speedMs === null ? null : sample.speedMs * 3.6,
      altitude,
      {
        visible: prefs.showSpeedAltitude,
        showAltitude: prefs.showAltitude,
        textSizeLevel: prefs.speedAltitudeSize,
      },
    )
  }

  /**
   * Demande la grille du géoïde, une seule fois, et réaffiche à son arrivée.
   *
   * Sans ce réaffichage, la correction n'était appliquée qu'au relevé suivant.
   * Or le navigateur cesse d'en émettre quand la position ne bouge plus, et le
   * suivi écarte lui-même les relevés qui n'ont pas bougé d'un mètre : à
   * l'arrêt — au départ d'une course, justement — l'altitude restait
   * ellipsoïdale, soit environ 45 m de trop en France, jusqu'à ce qu'on roule.
   */
  #ensureGeoid(): void {
    if (this.#geoidRequested) return
    this.#geoidRequested = true

    void this.#geoid.load().then(() => this.#refreshSpeedAltitude())
  }

  /** Repère et cercle de précision, dessinés hors suivi. */
  #drawPassivePosition(): void {
    const sample = this.#lastSample
    if (sample === null || this.#following) return

    this.#updatePuck(sample.lat, sample.lon, 0)
    this.#layers.setAccuracyCircle({
      lat: sample.lat,
      lon: sample.lon,
      accuracy: sample.accuracy,
    })
  }

  /**
   * Correction manuelle de la position sur la trace.
   *
   * Sur une boucle ou un aller-retour, l'accrochage peut se verrouiller sur le
   * mauvais passage ; ce dialogue laisse désigner le bon point kilométrique.
   */
  /**
   * Points kilométriques auxquels la position accrochée pourrait correspondre.
   *
   * Vide dès que la trace demandée n'est pas celle où l'on est accroché : la
   * correction déplace la position le long d'une trace, elle ne saute pas de
   * l'une à l'autre. Une trace qui ne repasse jamais au même endroit n'en
   * renvoie qu'un, et il n'y a alors rien à corriger.
   */
  #fixPositionCandidates(track: GpxTrack): readonly { distance: number }[] {
    const snapped = this.#navigation.snappedTrack
    const snap = this.#navigation.snapInfo
    if (snapped === null || snap === null || snapped.id !== track.id) return []

    return track.allDistanceCandidates(snap.lat, snap.lon)
  }

  #showFixPositionDialog(): void {
    const track = this.#navigation.snappedTrack
    const t = this.#translator

    if (track === null) {
      this.#shell.showSnackbar(t.t('location_unavailable'))
      return
    }

    const candidates = this.#fixPositionCandidates(track)

    if (candidates.length < 2) {
      this.#shell.showSnackbar(t.t('location_unavailable'))
      return
    }

    chooseDialog(document.body, {
      title: t.t('long_press_choose_km'),
      cancelLabel: t.t('cancel'),
      choices: candidates.map((candidate) => ({
        value: candidate.distance,
        label: t.t('long_press_km_option', {
          value: t.number(track.rawDistanceToMarkerKm(candidate.distance), 2),
        }),
      })),
      onChoose: (distance) => {
        this.#navigation.setCurvilinearDistance(distance)
        this.#tracker.fixPosition(track.id, distance / 1000)
        this.#liveHud.refresh()
      },
    })
  }

  /* -------------------------------------------------------------------
   * Import et gestion des traces
   * ---------------------------------------------------------------- */

  /**
   * Le champ de sélection de fichiers.
   *
   * Il est **attaché au document et réutilisé**, et ces deux points sont la
   * correction d'un défaut, pas une commodité : un champ construit à la volée
   * et jamais inséré ouvre bien le sélecteur sur WebKit, mais l'événement
   * `change` ne part pas — il n'a aucun arbre où remonter. L'import restait
   * alors sans effet **et sans message**, puisque le code d'import n'était
   * jamais atteint. C'est ce qu'a montré un message d'erreur inconditionnel
   * qui ne s'affichait jamais.
   *
   * Le réutiliser plutôt que d'en créer un par appel évite d'accumuler un
   * élément mort à chaque sélection abandonnée.
   */
  #ensureFileInput(): HTMLInputElement {
    if (this.#fileInput !== null) return this.#fileInput

    const input = document.createElement('input')
    input.type = 'file'
    // Le filtre dépend de la plateforme, et `core/filePicker` dit pourquoi :
    // ce qui rend la sélection possible sur iOS est précisément ce qui la rend
    // permissive ailleurs. Il n'est de toute façon qu'une indication —
    // `looksLikeGpx` tranche à l'import, sur le contenu.
    input.accept = gpxAcceptAttribute(currentPickerEnvironment())
    input.multiple = true

    // Hors de l'écran plutôt que `display: none` ou `hidden` : WebKit refuse
    // d'ouvrir le sélecteur d'un champ qui n'est pas rendu, ce qui ramènerait
    // au défaut qu'on corrige. Il occupe donc une place réelle, mais nulle.
    input.style.cssText =
      'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none'
    input.tabIndex = -1
    input.setAttribute('aria-hidden', 'true')

    /*
     * `change` et `input` sont écoutés tous les deux : selon les versions de
     * WebKit, l'un des deux seulement accompagne une sélection. Le drapeau
     * évite d'importer deux fois quand ils partent ensemble, cas le plus
     * fréquent.
     */
    const onSelection = (): void => {
      if (this.#selectionHandled) return
      this.#selectionHandled = true

      const files = Array.from(input.files ?? [])

      // La valeur est vidée pour que le choix du même fichier une seconde fois
      // produise bien un nouvel événement : sans cela, un import relancé après
      // un échec resterait silencieux.
      input.value = ''

      // Le `catch` n'est pas décoratif : sans lui, toute erreur échappée de
      // l'import devient un rejet non traité, que rien n'affiche. C'est ce qui
      // rendait un import raté indiscernable d'une application figée.
      void this.#importFiles(files).catch((error: unknown) => {
        console.error('[VeloGPX] Import interrompu', error)
        this.#shell.showSnackbar(
          this.#translator.t('error_import_failed', {
            reason: error instanceof Error ? error.message : String(error),
          }),
        )
      })
    }

    input.addEventListener('change', onSelection)
    input.addEventListener('input', onSelection)

    document.body.append(input)
    this.#fileInput = input
    return input
  }

  #pickFiles(): void {
    // Réarmé ici et non à la fin de l'import : les deux événements d'une même
    // sélection se suivent de trop près pour qu'un drapeau relâché en fin
    // d'import les distingue, et le second — valeur déjà vidée — annoncerait
    // une sélection vide qui n'a pas eu lieu.
    this.#selectionHandled = false
    this.#ensureFileInput().click()
  }

  /**
   * Demande ce qu'il faut retenir d'un fichier.
   *
   * Un fichier ne portant que des traces n'a rien d'ambigu : aucune question
   * n'est posée. Le choix est ensuite enregistré avec la trace et rejoué à la
   * restauration, sans quoi un import « traces seules » réintroduirait les
   * points d'intérêt au redémarrage suivant.
   *
   * @returns `null` si l'utilisateur renonce, auquel cas le fichier est
   *   ignoré.
   */
  #askImportChoice(parsed: {
    tracks: readonly unknown[]
    waypoints: readonly unknown[]
  }): Promise<{ includeTracks: boolean; includeWaypoints: boolean } | null> {
    const hasTracks = parsed.tracks.length > 0
    const hasWaypoints = parsed.waypoints.length > 0

    // Un fichier sans point d'intérêt ne pose pas la question, et la réponse
    // vaut « oui » : `includeFileWaypoints` à `false` signifie « le fichier en
    // avait, la trace les a déclinés », et c'est ce que lit l'interrupteur des
    // réglages pour savoir s'il a quelque chose à proposer. Répondre « non »
    // ici le faisait apparaître sur des traces dont le fichier n'a rien à
    // rendre.
    if (!hasWaypoints) {
      return Promise.resolve({ includeTracks: true, includeWaypoints: true })
    }

    const t = this.#translator
    const choices: {
      value: { includeTracks: boolean; includeWaypoints: boolean }
      label: string
    }[] = []

    // Trois choix ici contre quatre dans l'APK : « points d'intérêt +
    // itinéraire routier » n'existe pas, faute d'équivalent au routage à
    // l'import côté web — le portage propose le routage *après* coup, quand
    // l'écart médian trahit une trace tracée à la main. Écart assumé, au
    // tableau du README.
    if (hasTracks) {
      choices.push(
        {
          value: { includeTracks: true, includeWaypoints: true },
          label: t.t('import_all', { waypoints: parsed.waypoints.length }),
        },
        {
          value: { includeTracks: true, includeWaypoints: false },
          label: t.t('import_tracks_only'),
        },
      )
    }

    choices.push({
      value: { includeTracks: false, includeWaypoints: true },
      label: t.t('import_waypoints_only', { count: parsed.waypoints.length }),
    })

    // Un fichier de points seuls n'appelle pas de question s'il n'y a qu'une
    // réponse possible.
    if (choices.length === 1) return Promise.resolve(choices[0]!.value)

    return new Promise((resolve) => {
      let answered = false

      chooseDialog(document.body, {
        title: t.t('import_choice_title'),
        cancelLabel: t.t('cancel'),
        choices,
        onChoose: (value) => {
          answered = true
          resolve(value)
        },
        onClose: () => {
          if (!answered) resolve(null)
        },
      })
    })
  }

  /**
   * Fait choisir **la** trace, quand le fichier en porte plusieurs.
   *
   * Un GPX importé donne une trace dans l'application, et une trace à
   * l'export. C'est l'hypothèse sur laquelle repose le rejeu du fichier
   * source : un fichier exporté ne porte qu'un `<trk>`, et il faut donc savoir
   * lequel garder.
   *
   * Rend `null` à l'abandon, ce qui écarte **ce fichier seulement** — les
   * autres fichiers d'une sélection multiple continuent d'être importés.
   *
   * Aucune présélection, et aucun « tout importer » : la règle est stricte, et
   * reprendre une autre trace se fait en réimportant le fichier, ce qui
   * réutilise la copie déjà en dépôt.
   */
  #askTrackSelection(
    tracks: readonly ParsedTrack[],
    fileName: string,
  ): Promise<number | null> {
    const t = this.#translator

    // Le nom de fichier au titre : sans lui, enchaîner plusieurs GPX ne dit
    // plus lequel on est en train de traiter.
    const title = t.t('import_select_track_title_named', { name: fileName })

    const choices = tracks.map((track, index) => {
      // `name` porte le nom de fichier et vaut donc la même chose pour toutes
      // les traces du fichier : seul `fileTrackName` les distingue.
      const label =
        track.fileTrackName ??
        t.t('import_track_fallback_name', { index: index + 1 })

      const meters = totalDistanceOf(track.segments)
      return {
        value: index,
        label: t.t('import_track_entry', {
          name: label,
          km: (meters / 1000).toFixed(1),
        }),
      }
    })

    return new Promise((resolve) => {
      let answered = false

      chooseDialog(document.body, {
        title,
        cancelLabel: t.t('cancel'),
        choices,
        onChoose: (value) => {
          answered = true
          resolve(value)
        },
        onClose: () => {
          if (!answered) resolve(null)
        },
      })
    })
  }

  async #importFiles(files: readonly File[]): Promise<void> {
    // Une sélection vide n'est pas un renoncement de l'utilisateur : iOS rend
    // `files` vide quand le fournisseur — iCloud au premier chef — n'a pas pu
    // livrer le fichier, typiquement parce qu'il n'est pas téléchargé sur
    // l'appareil. Sans ce retour, l'import échouait sans rien afficher, ce qui
    // ne se distingue pas d'une application figée.
    if (files.length === 0) {
      this.#shell.showSnackbar(this.#translator.t('error_import_empty_selection'))
      return
    }

    const imported: GpxTrack[] = []

    // Déduplication par nom : les fournisseurs de stockage en ligne exposent
    // parfois plusieurs fois le même fichier dans une sélection.
    const seen = new Set<string>()
    let rejected = 0

    for (const file of files) {
      if (!isGpxFileName(file.name)) {
        rejected += 1
        continue
      }

      if (seen.has(file.name)) continue
      seen.add(file.name)

      /*
       * Le plafond de taille, avant toute lecture.
       *
       * L'APK borne son flux d'entrée à la même valeur (`GpxParser.kt`), mais
       * le dépassement y est **muet** : l'exception est journalisée et l'URI
       * abandonnée sans un mot. Écart délibéré, au tableau du `README.md` — un
       * import qui ne dit rien est indiscernable d'une application figée, et le
       * dépôt a déjà payé deux fois ce mode de panne.
       *
       * Le navigateur donne la taille sans ouvrir le fichier, là où Android
       * doit compter les octets au fil de la lecture : on refuse donc **avant**
       * de charger, plutôt qu'au milieu.
       */
      if (file.size > LIMITS.MAX_GPX_FILE_SIZE) {
        this.#shell.showSnackbar(
          this.#translator.t('error_gpx_too_large', {
            name: file.name,
            limit: Math.round(LIMITS.MAX_GPX_FILE_SIZE / (1024 * 1024)),
          }),
        )
        continue
      }

      try {
        // L'en-tête avant le fichier entier : un fichier étranger — ou un
        // fichier énorme renommé `.gpx`, ce que le sélecteur d'iOS permet — est
        // écarté sans jamais être chargé en mémoire.
        const head = await file.slice(0, GPX_SNIFF_BYTES).text()

        // Un fichier iCloud resté à l'état d'emplacement réservé porte le bon
        // nom mais aucun contenu. Le signaler ici plutôt que de laisser
        // l'analyse échouer : « ce fichier n'est pas un GPX » désignerait le
        // fichier, alors que c'est son téléchargement qui manque.
        if (head.trim().length === 0) {
          this.#shell.showSnackbar(
            this.#translator.t('error_import_empty_file', { name: file.name }),
          )
          continue
        }

        // L'extension ne prouve rien : le sélecteur d'iOS ne sait pas filtrer
        // les GPX, et `parseGpx` tronque plutôt que d'échouer. Sans ce
        // contrôle, un fichier étranger renommé `.gpx` produisait une trace
        // vide, importée sans un mot.
        if (!looksLikeGpx(head)) {
          rejected += 1
          continue
        }

        const content = await file.text()

        const baseName = file.name.replace(/\.gpx$/i, '')
        const parsed = await parseGpxAsync({
          source: content,
          fileName: baseName,
          defaultTrackName: this.#translator.t('track_default_name', { index: '' }).trim(),
        })

        // Un GPX bien formé mais vide de trace comme de point d'intérêt
        // n'ajoutait rien et ne disait rien : l'import ne se distinguait alors
        // pas d'une application figée. Le cas est réel — un exporteur qui
        // n'écrit que des métadonnées —, et distinct d'un fichier qui n'est
        // pas un GPX.
        if (parsed.tracks.length === 0 && parsed.waypoints.length === 0) {
          this.#shell.showSnackbar(
            this.#translator.t('error_gpx_no_content', { name: file.name }),
          )
          continue
        }

        // Un fichier portant à la fois des traces et des points d'intérêt est
        // ambigu : l'organisateur a pu vouloir l'un, l'autre, ou les deux.
        const choice = await this.#askImportChoice(parsed)
        if (choice === null) continue

        // Un GPX importé donne **une** trace. Quand le fichier en porte
        // plusieurs, il faut choisir : c'est l'hypothèse sur laquelle repose le
        // rejeu du fichier source à l'export, qui ne rend qu'un `<trk>`.
        //
        // La question ne se pose qu'aux choix qui retiennent la trace : « points
        // d'intérêt uniquement » n'a que faire d'une trace choisie.
        let selectedTrackIndex: number | null = null
        if (choice.includeTracks && parsed.tracks.length > 1) {
          selectedTrackIndex = await this.#askTrackSelection(
            parsed.tracks,
            file.name,
          )
          // Abandon : ce fichier seulement est écarté, la sélection continue.
          if (selectedTrackIndex === null) continue
        }

        // Réimporter le même fichier pour en prendre une autre trace réutilise
        // la copie déjà en dépôt plutôt que de la dupliquer — c'est le mode
        // d'emploi assumé, il ne doit pas coûter un second exemplaire.
        const fileKey = await Storage.fileKeyFor(content)
        await this.#storage.putFile(fileKey, content)

        // Les points d'intérêt appartiennent au **fichier**, et une seule trace
        // les porte. Réimporter le même fichier pour en prendre une autre
        // trace — le mode d'emploi assumé — les aurait sinon donnés à la
        // nouvelle aussi, puisqu'elle est la première de *son* import : ils se
        // seraient dessinés deux fois, et les deux traces se seraient crues
        // propriétaires.
        const alreadyOwned = this.#tracks.some(
          (other) =>
            other.includeFileWaypoints &&
            this.#storedByTrackId.get(other.id)?.fileKey === fileKey,
        )

        // Les positions sont posées **avant** tout filtrage : après, la
        // deuxième trace d'un fichier repartirait avec l'index 0, et la
        // restauration, le retour au tracé d'origine et l'export viseraient
        // tous les trois le mauvais `<trk>`.
        const numbered = parsed.tracks.map((parsedTrack, fileIndex) => ({
          parsedTrack,
          fileIndex,
        }))

        const tracks = !choice.includeTracks
          ? []
          : selectedTrackIndex === null
            ? numbered
            : numbered.filter((entry) => entry.fileIndex === selectedTrackIndex)

        const ownsWaypoints = (index: number): boolean =>
          ownsFileWaypointsAtImport(index, choice.includeWaypoints, alreadyOwned)

        for (const [index, { parsedTrack, fileIndex }] of tracks.entries()) {
          const track = new GpxTrack({
            id: crypto.randomUUID(),
            name: parsedTrack.name ?? baseName,
            segments: parsedTrack.segments,
            waypoints: ownsWaypoints(index) ? parsed.waypoints : [],
            color: pickTrackColor(this.#tracks.map((t) => t.color)),
            sourceType: assembledSourceType(true, parsed.waypointSourceType),
          })

          track.realStartDistance = parsedTrack.realStartDistance
          track.kmMarkersFromFictiveStart = parsedTrack.kmMarkersFromFictiveStart
          track.customMarkers = [...parsedTrack.customMarkers]
          track.wayInOuts = [...parsedTrack.wayInOuts]
          // Le fichier se déclare routé : ses `<trkseg>` sont déjà le résultat
          // d'un calcul. La trace l'est donc aussi, sans quoi les réglages lui
          // proposeraient de « revenir » à un tracé d'origine qu'elle n'a pas.
          track.isRouted = parsedTrack.routed
          track.fileDeclaresRouted = parsedTrack.routed
          if (parsedTrack.routingProfile !== null) {
            track.routingProfile = ROUTING_PROFILES[parsedTrack.routingProfile]
          }
          track.fileTrackIndex = fileIndex
          track.includeFileTracks = true
          track.includeFileWaypoints = ownsWaypoints(index)

          for (const [wayId, route] of parsedTrack.wayRoutes) {
            track.wayRoutes.set(
              wayId,
              route.map((p) => ({
                lat: p.lat,
                lon: p.lon,
                ele: null,
                time: null,
                name: null,
              })),
            )
          }

          // Les marqueurs dérivés ne viennent pas du fichier : ils se
          // déduisent des détours, dont ils ne sont que le reflet.
          track.rebuildWayOutMarkers(this.#wayOutSymbol())

          this.#tracks.push(track)
          imported.push(track)
          this.#storedByTrackId.set(track.id, this.#toStored(track, fileKey))
        }

        // Un fichier sans `<trk>` — ou dont on n'a retenu que les points —
        // reste exploitable : une trace porteuse de ces seuls points.
        if (tracks.length === 0 && choice.includeWaypoints && parsed.waypoints.length > 0) {
          const track = new GpxTrack({
            id: crypto.randomUUID(),
            name: baseName,
            waypoints: parsed.waypoints,
            color: pickTrackColor(this.#tracks.map((t) => t.color)),
            sourceType: assembledSourceType(false, parsed.waypointSourceType),
          })
          track.includeFileTracks = false
          track.includeFileWaypoints = true
          this.#tracks.push(track)
          imported.push(track)
          this.#storedByTrackId.set(track.id, this.#toStored(track, fileKey))
        }
      } catch (error) {
        console.error('[VeloGPX] Import impossible', error)
        this.#shell.showSnackbar(this.#translator.t('error_not_gpx_file'))
      }
    }

    if (rejected > 0) {
      this.#shell.showSnackbar(
        this.#translator.t(
          rejected === 1 ? 'error_not_gpx_file' : 'error_not_gpx_files',
          { count: rejected },
        ),
      )
    }

    if (imported.length === 0) return

    // La pose des couches est isolée du reste de l'import.
    //
    // Elle échoue quand la carte n'est pas en état de recevoir une source —
    // contexte WebGL perdu, style pas encore chargé. Comme l'appel se trouvait
    // hors de toute protection, l'exception emportait le rafraîchissement de
    // la liste et l'enregistrement : la trace était analysée et rangée en
    // mémoire, mais restait invisible et l'application muette. Un défaut
    // d'affichage doit coûter l'affichage, pas l'import.
    let undrawn = 0
    for (const track of imported) {
      try {
        this.#layers.addTrack(track)
      } catch (error) {
        undrawn += 1
        console.error('[VeloGPX] Trace non dessinée', error)
      }
    }

    if (undrawn > 0) {
      this.#shell.showSnackbar(this.#translator.t('error_track_not_drawn'))
    }

    this.#refreshTrackList()
    await this.#persistTracks()

    // Un GPX exporté par l'application embarque la géométrie de ses détours,
    // qu'on met simplement en cache. Sans elle — export d'une autre source,
    // ancien fichier — le calcul se fait en silence : le réseau est souvent
    // saturé sur une ligne de départ, et l'échec n'a rien à signaler.
    for (const track of imported) {
      void this.#ways.ensureRoutes(track)
    }

    // L'ordre compte, et il est imposé : le routage passe **avant** la
    // proposition d'altitude, et l'import l'attend. Ces fichiers portent en
    // général `ele=0` partout ; récupérer le profil d'abord le calculerait sur
    // les cordes, donc à refaire. Le filtre d'altitude est ainsi réévalué sur
    // la géométrie routée.
    const sparse = imported.filter((track) => isSparseTrack(track))
    if (sparse.length > 0 && (await this.#askSparseRouting(sparse))) {
      // En séquence, pas en parallèle : le serveur OSRM est public.
      for (const track of sparse) {
        await this.#route.routeSparse(track)
      }
    }

    // Une seule proposition pour tout le lot : une par trace serait pénible
    // sur un fichier qui en contient plusieurs.
    const withoutElevation = imported.filter((track) =>
      this.#elevation.needsElevation(track),
    )

    if (withoutElevation.length > 0) {
      const t = this.#translator
      showDialog(document.body, {
        title: t.t('elevation_offer_title'),
        body: t.t('elevation_offer_message', { count: withoutElevation.length }),
        dismissible: true,
        actions: [
          { label: t.t('elevation_offer_later') },
          {
            label: t.t('ok'),
            primary: true,
            onClick: () => {
              for (const track of withoutElevation) {
                void this.#elevation.fetch(track)
              }
            },
          },
        ],
      })
    }

    const last = imported[imported.length - 1]
    if (last) this.#fitTrack(last)
    this.#drawer.close()
  }

  /**
   * Propose de router les traces posées à la main, une fois pour le lot.
   *
   * Refuser ne coûte rien : « Calculer l'itinéraire routier » reste dans les
   * réglages de chaque trace. C'est la découverte qui compte — aujourd'hui, le
   * défaut ne se constate qu'en roulant, quand l'accrochage lâche.
   */
  #askSparseRouting(sparse: readonly GpxTrack[]): Promise<boolean> {
    const t = this.#translator

    // Le minimum des médianes : l'annonce doit décrire le cas le plus fin du
    // lot, sinon elle promet un défaut plus grave qu'il n'est sur l'une des
    // traces.
    const spacing = Math.trunc(
      Math.min(
        ...sparse.map((track) => medianPointSpacing(track.cumulativeDistances)),
      ),
    )

    return new Promise((resolve) => {
      let answered = false

      showDialog(document.body, {
        title: t.t('route_sparse_title'),
        body: t.t(
          sparse.length === 1
            ? 'route_sparse_prompt_one'
            : 'route_sparse_prompt_other',
          { count: sparse.length, spacing },
        ),
        dismissible: true,
        actions: [
          { label: t.t('route_sparse_later') },
          {
            label: t.t('route_sparse_go'),
            primary: true,
            onClick: () => {
              answered = true
              resolve(true)
            },
          },
        ],
        // Abandonner — bouton, échappement, clic dehors — vaut refus.
        onClose: () => {
          if (!answered) resolve(false)
        },
      })
    })
  }

  /** Symbole des marqueurs dérivés des détours. */
  #wayOutSymbol(): string {
    return this.#prefs.symbolForType(MARKER_TYPE_WAYOUT)
  }

  /**
   * Relit les points d'origine d'une trace depuis son fichier conservé.
   *
   * C'est ce qui rend le routage réversible : le GPX n'est jamais modifié,
   * revenir au tracé d'origine ne demande donc rien au réseau.
   */
  /**
   * Prend ou rend les points d'intérêt du fichier source d'une trace.
   *
   * Les rendre suffit à les oublier ; les reprendre demande de **relire le
   * fichier**, puisqu'ils ne sont gardés nulle part ailleurs — c'est ce que
   * fait `TrackSettingsController.setFileWaypointsShown`, et c'est ce qui
   * permet à l'import « la trace seule » d'être réversible sans réimporter.
   *
   * Rend `false` quand la relecture n'a rien donné : le fichier a pu
   * disparaître du dépôt. L'interrupteur revient alors sur sa position, plutôt
   * que d'annoncer des points que la trace n'a pas.
   */
  async #setFileWaypoints(track: GpxTrack, include: boolean): Promise<boolean> {
    if (!include) {
      // Masqués, pas oubliés : ils quittent la carte mais restent sur la trace,
      // sinon un export fait dans cet état rendrait un fichier sans le moindre
      // `<wpt>` — pour un simple réglage d'affichage.
      track.hiddenFileWaypoints = track.waypoints
      track.setWaypoints([])
      track.includeFileWaypoints = false
      return true
    }

    const waypoints = await this.#reloadFileWaypoints(track)
    if (waypoints === null || waypoints.length === 0) {
      this.#shell.showSnackbar(this.#translator.t('error_waypoints_reload'))
      return false
    }

    track.setWaypoints(waypoints)
    track.hiddenFileWaypoints = []
    track.includeFileWaypoints = true
    return true
  }

  /**
   * Une autre trace de la bibliothèque partage-t-elle le fichier source ?
   *
   * Comptage de références, comme à la suppression : c'est lui qui dit qui
   * possède les points d'intérêt du fichier. Rend `null` quand la trace n'a
   * pas de copie source — il n'y a alors rien à posséder.
   */
  #sharesFileWithOthers(track: GpxTrack): boolean | null {
    const stored = this.#storedByTrackId.get(track.id)
    if (!stored) return null

    return this.#tracks.some(
      (other) =>
        other !== track &&
        this.#storedByTrackId.get(other.id)?.fileKey === stored.fileKey,
    )
  }

  /** Relit les points d'intérêt du fichier conservé, sans toucher à la trace. */
  async #reloadFileWaypoints(track: GpxTrack): Promise<Waypoint[] | null> {
    const stored = this.#storedByTrackId.get(track.id)
    if (!stored) return null

    const content = await this.#storage.getFile(stored.fileKey)
    if (content === null) return null

    const parsed = await parseGpxAsync({ source: content, fileName: track.name })
    return [...parsed.waypoints]
  }

  async #reloadOriginalSegments(track: GpxTrack): Promise<TrackSegment[] | null> {
    const stored = this.#storedByTrackId.get(track.id)
    if (!stored) return null

    const content = await this.#storage.getFile(stored.fileKey)
    if (content === null) return null

    const parsed = await parseGpxAsync({ source: content, fileName: track.name })
    const parsedTrack = parsed.tracks[track.fileTrackIndex]
    if (!parsedTrack) return null

    // Les segments tels quels : aplatir ici referait, par l'autre bout, l'écart
    // que la récupération d'altitude vient de perdre — un retour au tracé
    // d'origine rendrait une trace d'un seul segment là où le fichier en a
    // plusieurs, et l'export ne dirait plus la même chose que l'APK.
    return parsedTrack.segments.map((segment) => ({ points: segment.points }))
  }

  #toStored(track: GpxTrack, fileKey: string): StoredTrack {
    return {
      id: track.id,
      order: this.#tracks.indexOf(track),
      name: track.name,
      color: track.color,
      visible: track.visible,
      realStartDistance: track.realStartDistance,
      kmMarkerInterval: track.kmMarkerInterval,
      kmMarkersFromFictiveStart: track.kmMarkersFromFictiveStart,
      isRouted: track.isRouted,
      routingProfile: track.routingProfile.id,
      sourceType: track.sourceType,
      customMarkers: persistableMarkers(track),
      wayInOuts: [...track.wayInOuts],
      fileTrackIndex: track.fileTrackIndex,
      includeFileTracks: track.includeFileTracks,
      includeFileWaypoints: track.includeFileWaypoints,
      fileKey,
      importedAt: Date.now(),
    }
  }

  async #persistTracks(): Promise<void> {
    const records: StoredTrack[] = []

    for (const [index, track] of this.#tracks.entries()) {
      const stored = this.#storedByTrackId.get(track.id)
      if (!stored) continue

      const updated: StoredTrack = {
        ...stored,
        order: index,
        name: track.name,
        color: track.color,
        visible: track.visible,
        realStartDistance: track.realStartDistance,
        kmMarkerInterval: track.kmMarkerInterval,
        kmMarkersFromFictiveStart: track.kmMarkersFromFictiveStart,
        // `isRouted` n'est pas décoratif : c'est lui que `RouteController.restore`
        // interroge au démarrage pour savoir s'il doit réappliquer l'itinéraire
        // en cache. Omis ici, une trace routée revenait à son tracé d'origine à
        // chaque redémarrage, alors que la géométrie était bien conservée.
        isRouted: track.isRouted,
        routingProfile: track.routingProfile.id,
        // Il change après l'import — l'interrupteur des réglages le bascule —
        // et l'enregistrement doit suivre, sans quoi les points d'intérêt
        // repris reviendraient absents au redémarrage.
        includeFileWaypoints: track.includeFileWaypoints,
        customMarkers: persistableMarkers(track),
        wayInOuts: [...track.wayInOuts],
      }

      // `showWaypoints` est hérité : sans ce retrait, `...stored` le recopie
      // indéfiniment et `restoredFileWaypoints` continuerait de le lire.
      delete updated.showWaypoints

      this.#storedByTrackId.set(track.id, updated)
      records.push(updated)
    }

    await this.#storage.saveTracks(records)
  }

  async #restoreTracks(): Promise<void> {
    const stored = await this.#storage.listTracks()
    if (stored.length === 0) return

    let failed = 0

    for (const record of stored) {
      try {
        await this.#restoreTrack(record)
      } catch (error) {
        // Une trace qui refuse de se restaurer — fichier corrompu, couche
        // impossible à poser — ne doit pas emporter les suivantes ni le
        // rafraîchissement de la liste. C'est le piège déjà payé à l'import.
        failed += 1
        console.error('[VeloGPX] Trace non restaurée', record.id, error)
      }
    }

    if (failed > 0) {
      this.#shell.showSnackbar(this.#translator.t('error_track_not_drawn'))
    }

    this.#refreshTrackList()

    // Géométries et altitudes complétées après coup : l'application est déjà
    // utilisable, et ces reprises silencieuses ne doivent pas retarder
    // l'affichage.
    for (const track of this.#tracks) {
      void this.#ways.ensureRoutes(track)
      void this.#route.restore(track).then(() => this.#elevation.restore(track))
    }
  }

  async #restoreTrack(record: StoredTrack): Promise<void> {
    const content = await this.#storage.getFile(record.fileKey)
    if (content === null) return

    const parsed = await parseGpxAsync({ source: content, fileName: record.name })
    const parsedTrack = parsed.tracks[record.fileTrackIndex]

    // Le fichier vient d'être relu : c'est le seul moment où l'on sait s'il
    // portait des points d'intérêt, donc le seul où les enregistrements
    // hérités peuvent être ramenés à l'unique drapeau.
    const includeFileWaypoints = restoredFileWaypoints(record, parsed.waypoints.length)

    const track = new GpxTrack({
      id: record.id,
      name: record.name,
      // Le choix fait à l'import est rejoué : sans lui, un import « points
      // d'intérêt seuls » réintroduirait les traces du fichier à chaque
      // redémarrage.
      segments: record.includeFileTracks ? (parsedTrack?.segments ?? []) : [],
      waypoints: includeFileWaypoints ? parsed.waypoints : [],
      color: record.color,
      visible: record.visible,
      // Recalculée plutôt que relue : les traces importées avant la
      // correction portent « WPT » en magasin, et s'en remettre à
      // l'enregistrement les laisserait mentir jusqu'à un réimport. Le
      // fichier vient d'être relu, et `includeFileTracks` dit si la trace
      // est née d'un `<trk>` — tout ce que la règle demande.
      sourceType: assembledSourceType(
        record.includeFileTracks,
        parsed.waypointSourceType,
      ),
    })

    track.realStartDistance = record.realStartDistance
    track.kmMarkerInterval = record.kmMarkerInterval
    track.kmMarkersFromFictiveStart = record.kmMarkersFromFictiveStart
    track.isRouted = record.isRouted
    track.routingProfile = routingProfileFromName(record.routingProfile)
    track.fileDeclaresRouted = parsedTrack?.routed ?? false
    // Normalisé à la relecture comme au parse : un marqueur enregistré avant
    // que les types ne soient fermés peut porter un type que plus rien ne sait
    // afficher.
    track.customMarkers = record.customMarkers.map((marker) => ({
      ...marker,
      type: normalizeMarkerType(marker.type),
    }))
    track.wayInOuts = [...record.wayInOuts]
    track.fileTrackIndex = record.fileTrackIndex
    track.includeFileTracks = record.includeFileTracks
    track.includeFileWaypoints = includeFileWaypoints
    // Le fichier vient d'être relu : c'est le moment de reprendre ce que le
    // commutateur masque, sans quoi un export fait après un redémarrage
    // perdrait des points que la session précédente savait rendre.
    track.hiddenFileWaypoints = includeFileWaypoints ? [] : parsed.waypoints

    // Les détours enregistrés font foi — leurs identifiants sont stables —
    // mais la géométrie routée, elle, n'est jamais enregistrée avec eux. Si
    // le cache a été purgé, celle embarquée dans le fichier la remplace ;
    // les identifiants ne survivant pas au GPX, on la retrouve en comparant
    // les définitions.
    track.rebuildWayOutMarkers(this.#wayOutSymbol())
    if (track.wayInOuts.length > 0 && parsedTrack) {
      this.#ways.adoptFileRoutes(
        track,
        parsedTrack.wayInOuts,
        parsedTrack.wayRoutes,
      )
    }

    // La trace rejoint la liste avant d'être dessinée : un échec de pose des
    // couches ne doit coûter que l'affichage, pas la trace elle-même.
    this.#tracks.push(track)
    this.#storedByTrackId.set(track.id, record)
    this.#layers.addTrack(track)
  }

  #refreshTrackList(): void {
    this.#drawer.setTracks(this.#tracks)
    this.#layers.setTrackOrder(this.#tracks.map((track) => track.id))
  }

  #reorderTracks(order: readonly string[]): void {
    const byId = new Map(this.#tracks.map((track) => [track.id, track]))
    this.#tracks = order
      .map((id) => byId.get(id))
      .filter((track): track is GpxTrack => track !== undefined)

    this.#refreshTrackList()
    for (const track of this.#tracks) {
      this.#layers.updateTrack(track)
    }
    void this.#persistTracks()
  }

  /* -------------------------------------------------------------------
   * Menu d'une trace
   * ---------------------------------------------------------------- */

  #showTrackMenu(trackId: string, anchor: HTMLElement): void {
    const track = this.#tracks.find((t) => t.id === trackId)
    if (!track) return

    const t = this.#translator
    const menu = document.createElement('div')
    menu.className = 'menu'

    const close = (): void => {
      menu.remove()
      document.removeEventListener('click', onOutsideClick, true)
    }

    const onOutsideClick = (event: MouseEvent): void => {
      if (!menu.contains(event.target as Node)) close()
    }

    const item = (
      label: string,
      icon: Parameters<typeof createIcon>[0] | null,
      onClick: () => void,
      danger = false,
    ): HTMLElement => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = `menu__item${danger ? ' menu__item--danger' : ''}`
      if (icon) button.append(createIcon(icon, 20))
      button.append(document.createTextNode(label))
      button.addEventListener('click', () => {
        close()
        onClick()
      })
      return button
    }

    menu.append(
      item(
        track.visible ? t.t('hide_track') : t.t('show_track'),
        'visibility',
        () => {
          track.visible = !track.visible
          this.#layers.updateTrack(track)
          this.#refreshTrackList()
          void this.#persistTracks()
        },
      ),
      item(t.t('track_info'), 'info', () => this.#showTrackInfo(track)),
      item(t.t('track_settings'), 'settings', () =>
        this.#trackSettings.show(track),
      ),
      item(t.t('export_gpx'), 'export', () => {
        // Le rejeu relit le fichier source depuis IndexedDB, donc l'export est
        // devenu asynchrone. Un échec ne doit pas rester muet : c'est
        // exactement le mode de panne que l'import a déjà payé.
        void this.#exportTrack(track).catch((error: unknown) => {
          console.error('[VeloGPX] Export impossible', error)
          this.#shell.showSnackbar(t.t('export_gpx_error'))
        })
      }),
      item(
        t.t('delete_track'),
        'delete',
        () =>
          confirmDialog(document.body, {
            title: t.t('delete_track_confirm_title'),
            message: t.t('delete_track_confirm_message', { name: track.name }),
            confirmLabel: t.t('delete_track'),
            cancelLabel: t.t('cancel'),
            onConfirm: () => this.#deleteTrack(track),
          }),
        true,
      ),
    )

    /*
     * Posé d'abord, placé ensuite : la hauteur du menu dépend du nombre
     * d'entrées, de la police et de la langue, et ne se connaît qu'une fois
     * dessiné. Le placer avant reviendrait à deviner, ce que faisait la
     * version précédente — le menu de la dernière trace sortait alors sous le
     * bord de l'écran, ses dernières entrées hors d'atteinte.
     *
     * Invisible le temps de la mesure, pour qu'aucune image ne le montre à sa
     * position provisoire. `visibility` et non `hidden` : un élément retiré de
     * la mise en page n'a pas de boîte à mesurer.
     */
    menu.style.visibility = 'hidden'
    this.overlayElement.append(menu)

    const placement = placeMenu({
      anchor: anchor.getBoundingClientRect(),
      menu: { width: menu.offsetWidth, height: menu.offsetHeight },
      bounds: this.overlayElement.getBoundingClientRect(),
    })

    menu.style.top = `${placement.top}px`
    menu.style.left = `${placement.left}px`
    if (placement.maxHeight !== null) {
      menu.style.maxHeight = `${placement.maxHeight}px`
      menu.style.overflowY = 'auto'
    }
    menu.style.visibility = ''

    // Différé d'un tour de boucle, sans quoi le clic qui ouvre le menu le
    // refermerait immédiatement.
    setTimeout(() => document.addEventListener('click', onOutsideClick, true), 0)
  }

  #showTrackInfo(track: GpxTrack): void {
    const t = this.#translator
    const body = document.createElement('div')

    /*
     * L'ordre est celui de `bottom_sheet_gpx_info.xml`, et non celui du code
     * Kotlin qui renseigne les vues : c'est la mise en page qui décide de ce
     * qu'on lit en premier. La nature de la trace vient donc juste après son
     * nom — elle qualifie tout ce qui suit, et reléguée en fin de liste elle se
     * lisait comme une note de bas de page.
     */
    const lines = [
      t.t('source_type_label', { value: track.sourceType }),
      t.t('info_distance_format', {
        value: t.number(track.totalDistance / 1000, 2),
      }),
    ]

    if (track.hasElevationData) {
      lines.push(
        t.t('info_elevation_gain_format', {
          value: Math.round(track.elevationGain),
        }),
        t.t('info_elevation_loss_format', {
          value: Math.round(track.elevationLoss),
        }),
        t.t('info_altitude_range_format', {
          min: Math.round(track.minElevation),
          max: Math.round(track.maxElevation),
        }),
      )
    }

    for (const line of lines) {
      const paragraph = document.createElement('p')
      paragraph.style.margin = '0 0 8px'
      paragraph.textContent = line
      body.append(paragraph)
    }

    // Section « Depuis le départ réel », comme la feuille d'infos d'Android :
    // sur une étape dont la neutralisation compte plusieurs kilomètres, ce sont
    // ces valeurs-là qui décrivent la course, pas celles du fichier.
    if (track.realStartDistance > 0) {
      const heading = document.createElement('p')
      heading.style.margin = '16px 0 8px'
      heading.style.fontWeight = '500'
      heading.textContent = t.t('real_start_section_label', {
        value: t.number(track.realStartDistance / 1000, 2),
      })
      body.append(heading)

      const fromReal = [
        t.t('info_distance_format', {
          value: t.number(track.distanceFromRealStart / 1000, 2),
        }),
      ]

      if (track.hasElevationData) {
        const { gain, loss } = track.gainLossFromDistance(track.realStartDistance)
        fromReal.push(
          t.t('info_elevation_gain_format', { value: Math.round(gain) }),
          t.t('info_elevation_loss_format', { value: Math.round(loss) }),
        )
      }

      for (const line of fromReal) {
        const paragraph = document.createElement('p')
        paragraph.style.margin = '0 0 8px'
        paragraph.textContent = line
        body.append(paragraph)
      }
    }

    // Le graphique s'accroche à ses gestes et suit la taille du dialogue : il
    // faut donc le défaire à la fermeture, ce qu'une simple insertion dans le
    // corps ne fait pas.
    const chart = track.hasElevationData ? createElevationChart(track) : null
    if (chart) {
      // Le graphique arrive sans rien qui le nomme : sur une feuille où tout le
      // reste est une phrase, une courbe nue se lit mal. Android lui donne un
      // titre (`textElevationProfileLabel`), et il n'y a pas de raison de s'en
      // passer.
      const heading = document.createElement('p')
      heading.style.margin = '16px 0 8px'
      heading.style.fontWeight = '500'
      heading.textContent = t.t('elevation_profile')
      body.append(heading, chart.element)
    }

    /*
     * Un dialogue, là où Android pose une feuille inférieure. Écart délibéré,
     * au tableau du `README.md` : la feuille a été portée puis retirée, son
     * glissement et celui du tiroir saccadant sur appareil sans qu'on ait pu
     * en établir la cause. Le dialogue, lui, n'anime rien et ne rate donc
     * rien.
     */
    showDialog(document.body, {
      title: track.name,
      body,
      actions: [{ label: t.t('close') }],
      onClose: () => chart?.destroy(),
    })
  }

  /**
   * Exporte la trace, au choix de l'utilisateur : enregistrement ou partage.
   *
   * Le fichier rendu est **celui qui a été importé**, dans lequel seules les
   * annotations VeloGPX et, si elle a bougé, la géométrie sont réécrites.
   * `<metadata>`, sections `CDATA`, `<rte>`, `<sym>`, horodatages et extensions
   * d'autres applications passent octet pour octet : un GPX d'organisateur
   * annoté puis renvoyé reste le sien.
   */
  async #exportTrack(track: GpxTrack): Promise<void> {
    const t = this.#translator

    const toWrite: TrackToWrite = {
      name: track.name,
      segments: track.segments,
      waypoints: track.waypoints,
      hiddenFileWaypoints: track.hiddenFileWaypoints,
      customMarkers: track.customMarkers,
      wayInOuts: track.wayInOuts,
      wayRoutes: track.wayRoutes,
      realStartDistance: track.realStartDistance,
      kmMarkersFromFictiveStart: track.kmMarkersFromFictiveStart,
      isRouted: track.isRouted,
      routingProfile: track.routingProfile,
    }

    const creator = `VeloGPX Web ${APP_VERSION}`
    const gpx =
      (await this.#patchedExport(track, toWrite, creator)) ??
      writeGpx(toWrite, { creator })

    const file = new File([gpx], `${sanitizeFileName(track.name)}.gpx`, {
      type: 'application/gpx+xml',
    })

    // Là où le système ne partage pas de fichier — les navigateurs de bureau,
    // pour l'essentiel —, l'enregistrement est la seule issue : un dialogue à
    // choix unique ne demanderait rien et ferait un geste de plus.
    if (!canShareFile(navigator, file)) {
      this.#saveExportedFile(file)
      return
    }

    chooseDialog(document.body, {
      title: t.t('export_gpx_dialog_title', { name: track.name }),
      choices: [
        { value: 'save' as const, label: t.t('export_gpx_save_local') },
        { value: 'share' as const, label: t.t('export_gpx_share') },
      ],
      cancelLabel: t.t('cancel'),
      onChoose: (value) => {
        if (value === 'save') {
          this.#saveExportedFile(file)
          return
        }

        void shareFile(navigator, file, track.name).then((outcome) => {
          // L'abandon ne dit rien : l'utilisateur a fermé le panneau, lui
          // répondre par un message d'échec serait lui reprocher son choix.
          if (outcome === 'failed') {
            this.#shell.showSnackbar(t.t('export_gpx_failed'))
          }
        })
      },
    })
  }

  /**
   * Rejoue le fichier source, ou rend `null` s'il n'est pas exploitable.
   *
   * Le repli n'est pas un échec : il coûte la fidélité — `<metadata>`, `<rte>`,
   * extensions tierces —, jamais l'export. Une trace importée avant que la
   * copie ne soit conservée, ou dont le dépôt a été purgé, ressort donc
   * reconstruite depuis le modèle.
   */
  async #patchedExport(
    track: GpxTrack,
    toWrite: TrackToWrite,
    creator: string,
  ): Promise<string | null> {
    const stored = this.#storedByTrackId.get(track.id)
    if (!stored) return null

    // Une trace née des seuls points d'intérêt n'a aucun `<trk>` dans son
    // fichier : il n'y a rien à patcher, et `fileTrackIndex` ne désigne rien.
    if (!track.includeFileTracks) return null

    let source: string | null = null
    try {
      source = await this.#storage.getFile(stored.fileKey)
    } catch (error) {
      console.error('[VeloGPX] Copie source illisible', error)
      return null
    }
    if (source === null) return null

    return patchGpx({
      source,
      fileTrackIndex: track.fileTrackIndex,
      track: toWrite,
      creator,
      geometryModified: track.geometryModified,
    })
  }

  #saveExportedFile(file: File): void {
    downloadFile(file)
    this.#shell.showSnackbar(this.#translator.t('export_gpx_saved'))
  }

  #deleteTrack(track: GpxTrack): void {
    const stored = this.#storedByTrackId.get(track.id)

    // L'accrochage, la progression et un éventuel déplacement de marqueur
    // doivent lâcher la trace avant sa disparition, sans quoi ils
    // continueraient de la référencer.
    this.#navigation.releaseTrack(track.id)
    this.#tracker.reset(track.id)
    this.#markers.releaseTrack(track.id)
    this.#picker.releaseTrack(track.id)
    this.#ways.onTrackDeleted(track)
    this.#elevation.cancel(track)
    this.#route.cancel(track)
    this.#markers.closePopup()

    this.#layers.removeTrack(track.id)
    this.#tracks = this.#tracks.filter((t) => t.id !== track.id)
    this.#storedByTrackId.delete(track.id)

    if (stored) {
      // La trace est déjà sortie de la liste : ce qui reste la référence est
      // ce qui doit garder le fichier.
      const stillUsed = this.#tracks.some(
        (other) => this.#storedByTrackId.get(other.id)?.fileKey === stored.fileKey,
      )
      void this.#storage.deleteTrack(track.id, stored.fileKey, {
        keepFile: stillUsed,
      })
    }

    this.#refreshTrackList()
    void this.#persistTracks()
  }

  #showColorPicker(trackId: string): void {
    const track = this.#tracks.find((t) => t.id === trackId)
    if (!track) return

    const grid = document.createElement('div')
    grid.className = 'color-grid'

    for (const color of [...TRACK_COLORS, ...EXTRA_COLORS]) {
      const swatch = document.createElement('button')
      swatch.type = 'button'
      swatch.className = 'color-swatch'
      swatch.style.background = color
      swatch.setAttribute('aria-label', color)
      swatch.setAttribute(
        'aria-pressed',
        String(color.toUpperCase() === track.color.toUpperCase()),
      )
      swatch.addEventListener('click', () => {
        track.color = color
        this.#layers.updateTrack(track)
        this.#refreshTrackList()
        void this.#persistTracks()
        close()
      })
      grid.append(swatch)
    }

    const close = showDialog(document.body, {
      title: this.#translator.t('change_color'),
      body: grid,
      actions: [{ label: this.#translator.t('cancel') }],
    })
  }
}

/**
 * Marqueurs à enregistrer.
 *
 * Ceux dérivés d'un détour en sont exclus : les détours sont la source de
 * vérité, et ces marqueurs sont reconstruits au chargement. Les enregistrer
 * produirait des doublons à chaque démarrage.
 */
function persistableMarkers(track: GpxTrack): CustomMarker[] {
  return track.customMarkers.filter(
    (marker) => marker.type !== MARKER_TYPE_WAYOUT,
  )
}

/**
 * Entité ponctuelle la plus proche du point touché.
 *
 * Les géométries linéaires — la ligne d'un détour — sont écartées : aucune
 * action ne s'y attache, et leur retenue masquerait un symbole voisin.
 */
function nearestFeature(
  features: readonly maplibregl.MapGeoJSONFeature[],
  point: maplibregl.Point,
  map: maplibregl.Map,
): maplibregl.MapGeoJSONFeature | null {
  let best: maplibregl.MapGeoJSONFeature | null = null
  let bestDistance = Infinity

  for (const feature of features) {
    if (feature.geometry.type !== 'Point') continue

    const [lon, lat] = feature.geometry.coordinates as [number, number]
    const projected = map.project([lon, lat])
    const distance = Math.hypot(projected.x - point.x, projected.y - point.y)

    if (distance < bestDistance) {
      bestDistance = distance
      best = feature
    }
  }

  return best
}
